import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from "@mui/material";
import { Plus, X, Check, CheckCircle2, RotateCcw, CornerDownRight, ArrowRight, GripVertical, HelpCircle, Pencil, Trophy, MoreVertical } from "lucide-react";
import { dataService } from "../../../../Services/data/data.service";
import { simulationScenarioTypes, controlTypes, externalApiTypes } from "../../../../Config/constants";
import { color } from "../../../Shared/tokens";
import { getMeasurementQty } from "../../../Shared/PriceBreakdown";
import { processPriceInput, parsePrice } from "../../../../Utils/priceInput";
import { fmt } from "../../../../Utils/formatCurrency";
import { toast } from "react-toastify";
import Button from "../../../Shared/Buttons/Button";
import Chip from "../../../Shared/ui/Chip";
import Input from "../../../Shared/Input";
import IconButton from "../../../Shared/Buttons/IconButton";
import SearchInput from "../../../Shared/SearchInput";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import SubstitutionDialog from "./SubstitutionDialog";
import DeleteScenarioDialog from "./DeleteScenarioDialog";
import CompositionEditorBody, { resolveCompositionRecipe, recipeDiffersFromBase, baseCompositionRecipe } from "./CompositionEditorBody";
import AllocationEditorBody, { resolveAllocationState, allocationStateToItems } from "./AllocationEditorBody";

// ScenarioType: 1 = Accounts, 2 = PriceDefinitions, 3 = Substitute, 4 = Composition

// ─── helpers ────────────────────────────────────────────────────────────────

function getScenarioTypeLabel(type, t) {
  switch (type) {
    case simulationScenarioTypes.ACCOUNTS: return t('projectionsTab.accounts');
    case simulationScenarioTypes.PRICE_DEFINITIONS: return t('projectionsTab.priceDefinitions');
    case simulationScenarioTypes.SUBSTITUTE: return t('projectionsTab.substitute');
    case simulationScenarioTypes.COMPOSITION: return t('projectionsTab.composition');
    case simulationScenarioTypes.ALLOCATE: return t('projectionsTab.allocate');
    default: return t('projectionsTab.priceDefinitions');
  }
}

// Shared column widths — keeps the param table and P&L table columns visually aligned.
// Both tables use table-layout:fixed with the same colgroup widths; since they share
// the same container width the first (label) column will also be identical.
const SIM_COL_W = 225; // px — Baseline/Current and every scenario column

function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}


function parseLocalNumber(str) {
  if (str == null) return NaN;
  return parseFloat(String(str).replace(/\./g, "").replace(",", "."));
}

// Extract all accounts from profitLossReportData as flat list with current totals
function extractAccounts(profitLossReportData) {
  if (!profitLossReportData) return [];
  const result = [];
  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      if (!cat.accountResults) return;
      cat.accountResults.forEach((acc) => {
        const total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        result.push({
          id: acc.id,
          name: acc.name,
          categoryName: cat.name,
          isRevenue: cat.isRevenue,
          group: cat.isRevenue ? "Revenue" : "Cost",
          currentValue: total,
        });
      });
    });
  };
  processCategories(profitLossReportData.revenueAccountCategoryResults);
  processCategories(profitLossReportData.costAccountCategoryResults);

  const groupOrder = { Revenue: 0, Cost: 1 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    const catCmp = (a.categoryName ?? "").localeCompare(b.categoryName ?? "");
    if (catCmp !== 0) return catCmp;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Extract all price definitions from priceBundles as flat deduped list,
// grouped by Revenue/Cost via P&L data and sorted alphabetically within each group.
function extractPriceDefinitions(priceBundles, profitLossReportData) {
  if (!priceBundles) return [];

  const revenueIds = new Set();
  const costIds = new Set();
  if (profitLossReportData) {
    (profitLossReportData.revenueAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => revenueIds.add(acc.id));
    });
    (profitLossReportData.costAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => costIds.add(acc.id));
    });
  }

  const seen = new Set();
  const result = [];
  priceBundles.forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      (bundle.items || []).forEach((item) => {
        if (!seen.has(item.priceDefinitionId)) {
          seen.add(item.priceDefinitionId);
          // Classify by the item's effective account (COALESCE(pdef.DefaultAccountId, pb.AccountId))
          // so priceDefinitions whose own account differs from the bundle-level account are correctly
          // grouped as Revenue or Cost instead of being shown as Uncategorized.
          const effId = item.effectiveAccountId ?? bundle.accountId;
          const group = revenueIds.has(effId) ? "Revenue" : costIds.has(effId) ? "Cost" : "Uncategorized";
          result.push({
            id: item.priceDefinitionId,
            name: item.priceDefinitionName,
            currentValue: item.price,
            uom: item.unitOfMeasurement || null,
            group,
          });
        }
      });
    });
  });

  const groupOrder = { Revenue: 0, Cost: 1, Uncategorized: 2 };
  result.sort((a, b) => {
    const go = (groupOrder[a.group] ?? 2) - (groupOrder[b.group] ?? 2);
    if (go !== 0) return go;
    return a.name.localeCompare(b.name);
  });

  return result;
}

// Build the list of substitutable price definitions: the union of definitions that appear in
// price bundles and definitions applied directly via junction rows. Each entry carries the scope
// options needed by the editor: which bundles contain it, and whether it is applied directly.
function extractSubstitutableDefinitions(allPriceBundles, junctionItems, profitLossReportData, codeById = {}) {
  const revenueIds = new Set();
  const costIds = new Set();
  if (profitLossReportData) {
    (profitLossReportData.revenueAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => revenueIds.add(acc.id));
    });
    (profitLossReportData.costAccountCategoryResults || []).forEach((cat) => {
      (cat.accountResults || []).forEach((acc) => costIds.add(acc.id));
    });
  }
  const classify = (effId) => (revenueIds.has(effId) ? "Revenue" : costIds.has(effId) ? "Cost" : "Uncategorized");

  const byId = new Map();
  const ensure = (id, name, code, price, uom, effId) => {
    if (!byId.has(id)) {
      byId.set(id, { id, name, code: codeById[id] ?? code ?? null, currentValue: price ?? 0, uom: uom || null, group: classify(effId), bundles: new Map(), hasDirect: false });
    }
    return byId.get(id);
  };

  (allPriceBundles || []).forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      (bundle.items || []).forEach((item) => {
        const e = ensure(item.priceDefinitionId, item.priceDefinitionName, item.priceDefinitionCode, item.price, item.unitOfMeasurement, item.effectiveAccountId ?? bundle.accountId);
        e.bundles.set(bundle.id, bundle.name);
      });
    });
  });

  (junctionItems || []).forEach((j) => {
    const e = ensure(j.priceDefinitionId, j.priceDefinitionName, j.priceDefinitionCode, j.unitPrice ?? 0, null, j.effectiveAccountId);
    e.hasDirect = true;
  });

  const result = Array.from(byId.values()).map((e) => ({
    id: e.id,
    name: e.name,
    code: e.code,
    currentValue: e.currentValue,
    uom: e.uom,
    group: e.group,
    bundles: Array.from(e.bundles.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    hasDirect: e.hasDirect,
  }));

  // Ordered by price-definition code (then name as a fallback when a code is missing).
  result.sort((a, b) => {
    const ac = a.code ?? "";
    const bc = b.code ?? "";
    const cmp = ac.localeCompare(bc, undefined, { numeric: true, sensitivity: "base" });
    if (cmp !== 0) return cmp;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
  return result;
}

// Compute projected P&L for accounts-type scenario
function computeAccountsPL(basePLD, items) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const itemMap = {};
  items.forEach((i) => { itemMap[i.itemId] = i.newValue; });

  const processCategories = (cats) => {
    if (!cats) return;
    cats.forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        acc.total = itemMap[acc.id] !== undefined ? itemMap[acc.id] : baseTotal;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };

  processCategories(data.revenueAccountCategoryResults);
  processCategories(data.costAccountCategoryResults);

  const revenueTotal = typeof data.revenueTotal === "object"
    ? Object.values(data.revenueTotal)[0] ?? 0
    : data.revenueTotal ?? 0;
  const costTotal = typeof data.costTotal === "object"
    ? Object.values(data.costTotal)[0] ?? 0
    : data.costTotal ?? 0;

  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Scales each account's baseline total by its projected/original ratio and recomputes
// the section totals. Shared by the price-definition and substitute P&L computations.
function applyAccountRatios(data, accountRatios) {
  const applyRatios = (cats) => {
    (cats || []).forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        const ratio = accountRatios[acc.id];
        if (ratio && ratio.original > 0) {
          // Money stayed on / passed through this account: scale the real baseline by the change.
          // Anchored to the baseline, so it can never go negative.
          acc.total = baseTotal * (ratio.projected / ratio.original);
        } else if (ratio && ratio.projected > 0) {
          // Pure gain: money arrived at an account that had no baseline contribution (a substitute
          // whose account was previously unused). Add it. (Never happens for the price-def path,
          // where every account has matching original+projected.)
          acc.total = baseTotal + ratio.projected;
        } else {
          acc.total = baseTotal;
        }
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };
  applyRatios(data.revenueAccountCategoryResults);
  applyRatios(data.costAccountCategoryResults);
  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Compute projected P&L for price-definitions-type scenario
function computePricePL(basePLD, items, allMeasurements, allPriceBundles) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  const pdMap = {};
  items.forEach((i) => { pdMap[i.itemId] = i.newValue; });

  if (!allMeasurements || !allPriceBundles) return data;

  // Build per-account ratios. For bundle measurements each item carries its own
  // effectiveAccountId (COALESCE(pdef.DefaultAccountId, pb.AccountId)), so a single
  // bundle measurement can contribute to multiple accounts. The eventPrice is split
  // proportionally across items using their share of the bundle total.
  const accountRatios = {};
  const addRatio = (accountId, original, projected) => {
    if (!accountId) return;
    if (!accountRatios[accountId]) accountRatios[accountId] = { original: 0, projected: 0 };
    accountRatios[accountId].original += original;
    accountRatios[accountId].projected += projected;
  };

  allMeasurements.forEach((m) => {
    const eventPrice = m.eventPrice || 0;

    if (m.priceBundleId) {
      let bundle = null;
      for (const company of allPriceBundles) {
        const found = (company.priceBundles || []).find((b) => b.id === m.priceBundleId);
        if (found) { bundle = found; break; }
      }
      if (bundle && (bundle.items || []).length > 0) {
        // Fixed items are applied once per measurement while hourly items scale with the
        // stopwatch duration, so items are weighted by qty before computing shares.
        const qty = getMeasurementQty(m);
        const originalBundleTotal = bundle.items.reduce((s, i) => s + i.price * i.multiplier * (i.isFixed ? 1 : qty), 0);
        if (originalBundleTotal > 0) {
          bundle.items.forEach((item) => {
            const weight = item.isFixed ? 1 : qty;
            const newP = pdMap[item.priceDefinitionId] !== undefined ? pdMap[item.priceDefinitionId] : item.price;
            const itemOriginal = (item.price * item.multiplier * weight) / originalBundleTotal;
            const itemProjected = (newP * item.multiplier * weight) / originalBundleTotal;
            addRatio(item.effectiveAccountId, eventPrice * itemOriginal, eventPrice * itemProjected);
          });
        } else {
          addRatio(m.accountId, eventPrice, eventPrice);
        }
      } else {
        addRatio(m.accountId, eventPrice, eventPrice);
      }
    } else {
      addRatio(m.accountId, eventPrice, eventPrice);
    }
  });

  return applyAccountRatios(data, accountRatios);
}

// Does a substitution's saved scope apply to a given occurrence?
// scope === null/undefined means "all occurrences".
function substitutionScopeMatches(scope, { bundleId, direct }) {
  if (!scope) return true;
  if (direct) return !!scope.direct;
  return Array.isArray(scope.bundleIds) && bundleId != null && scope.bundleIds.includes(bundleId);
}

// Decompose a measurement into its priced line items, honouring junction-overrides-bundle:
// if the measurement has junction rows they are used and the bundle is ignored.
// Returns null when the measurement has no decomposable line items (caller falls back to eventPrice).
function decomposeMeasurementLines(m, bundleById, junctionByMeasurement) {
  const junction = junctionByMeasurement?.[m.id];
  if (junction && junction.length > 0) {
    return junction.map((j) => ({
      priceDefinitionId: j.priceDefinitionId,
      price: j.unitPrice || 0,
      multiplier: j.multiplier ?? 1,
      isFixed: !!j.isFixed,
      effectiveAccountId: j.effectiveAccountId,
      direct: true,
    }));
  }
  if (m.priceBundleId) {
    const bundle = bundleById.get(m.priceBundleId);
    if (bundle && (bundle.items || []).length > 0) {
      return bundle.items.map((i) => ({
        priceDefinitionId: i.priceDefinitionId,
        price: i.price,
        multiplier: i.multiplier,
        isFixed: !!i.isFixed,
        effectiveAccountId: i.effectiveAccountId ?? bundle.accountId,
        bundleId: bundle.id,
        direct: false,
      }));
    }
  }
  return null;
}

function buildBundleById(allPriceBundles) {
  const map = new Map();
  (allPriceBundles || []).forEach((company) => {
    (company.priceBundles || []).forEach((b) => map.set(b.id, b));
  });
  return map;
}

// Compute projected P&L for a substitute-type scenario.
//
// A substitution *moves* money from a source account to the substitute's account, at the
// substitute's price. The substitute's account is its own DefaultAccountId when that account has a
// P&L row; otherwise the money lands on a synthetic row for the substitute itself
// ({ id: "sub-<substituteItemId>", isSubstitute: true }), placed in the source line's category, so
// the replacement stays visible instead of disappearing into a catch-all bundle account.
// Two forces, computed separately so neither distorts the other:
//
//   new(A) = baseline(A) * keptContrib(A) / totalContrib(A)          // money that stays on A
//          + Σ transfers-in, each valued as scaleFactor(source) * projectedContrib   // money moved in
//
// `scaleFactor(A) = baseline(A) / totalContrib(A)` converts an account's *client-decomposed* euros
// into its real *server-baseline* euros, cancelling any drift between the two. That anchoring is what
// keeps the result precise (a fully substituted source lands exactly on 0, an untouched account keeps
// exactly its baseline) and non-negative (the kept term is baseline * a fraction in [0, 1]).
//
// The old ratio model folded debit+credit into one projected/original ratio per account, which
// divided the incoming transfer by the *destination's* own decomposed baseline - so the moved amount
// (and the price change) came out wrong whenever the destination's decomposition drifted from its
// server baseline.
function computeSubstitutePL(basePLD, items, allMeasurements, allPriceBundles, junctionByMeasurement, subPriceById, subAccountById, subOptionsById) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  if (!allMeasurements) return data;

  // originalPriceDefinitionId -> { substituteItemId, price, scope, accountId (substitute's own DefaultAccountId or null) }
  const subMap = {};
  (items || []).forEach((it) => {
    if (it.substituteItemId == null) return;
    const price = subPriceById?.[it.substituteItemId];
    if (price == null) return; // unpriced substitute -> treated as no change
    subMap[it.itemId] = { substituteItemId: it.substituteItemId, price, scope: it.bundleScope || null, accountId: subAccountById?.[it.substituteItemId] ?? null };
  });

  const bundleById = buildBundleById(allPriceBundles);

  // Server baseline per account + the set of accounts that actually have a P&L row (a substitute can
  // only move money onto a displayed account).
  const baselineByAccount = {};
  const presentAccounts = new Set();
  [...(basePLD.revenueAccountCategoryResults || []), ...(basePLD.costAccountCategoryResults || [])]
    .forEach((cat) => (cat.accountResults || []).forEach((acc) => {
      baselineByAccount[acc.id] = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
      presentAccounts.add(acc.id);
    }));

  // Pass 1 - decompose every measurement into per-account contributions.
  //   totalContrib(A): all original contribution on A (the decomposition denominator).
  //   keptContrib(A):  contribution NOT substituted away.
  //   transfers:       substituted lines, credited to their destination account in pass 2.
  const totalContrib = {};
  const keptContrib = {};
  const transfers = []; // { sourceAccount, destAccount, projected }
  const add = (map, accountId, v) => { if (accountId != null) map[accountId] = (map[accountId] || 0) + v; };

  allMeasurements.forEach((m) => {
    const eventPrice = m.eventPrice || 0;
    const lines = decomposeMeasurementLines(m, bundleById, junctionByMeasurement);
    if (!lines) { add(totalContrib, m.accountId, eventPrice); add(keptContrib, m.accountId, eventPrice); return; }

    const qty = getMeasurementQty(m);
    const originalTotal = lines.reduce((s, it) => s + it.price * it.multiplier * (it.isFixed ? 1 : qty), 0);
    if (originalTotal <= 0) { add(totalContrib, m.accountId, eventPrice); add(keptContrib, m.accountId, eventPrice); return; }

    lines.forEach((it) => {
      const weight = it.isFixed ? 1 : qty;
      const contribOriginal = eventPrice * (it.price * it.multiplier * weight) / originalTotal;
      add(totalContrib, it.effectiveAccountId, contribOriginal);
      const sub = subMap[it.priceDefinitionId];
      const applies = sub && substitutionScopeMatches(sub.scope, { bundleId: it.bundleId, direct: it.direct });
      if (!applies) { add(keptContrib, it.effectiveAccountId, contribOriginal); return; }
      // Repriced contribution of the substituted line (same quantity/multiplier, substitute's price).
      const contribProjected = eventPrice * (sub.price * it.multiplier * weight) / originalTotal;
      // Substitute's own default account when it has a P&L row; otherwise (null) a synthetic row for
      // the substitute, created in pass 2.
      const destAccount = sub.accountId != null && presentAccounts.has(sub.accountId) ? sub.accountId : null;
      transfers.push({ sourceAccount: it.effectiveAccountId, destAccount, substituteItemId: sub.substituteItemId, projected: contribProjected });
    });
  });

  const scaleFactor = (accountId) => {
    const tc = totalContrib[accountId] || 0;
    return tc > 0 ? (baselineByAccount[accountId] || 0) / tc : 0;
  };

  // Pass 2 - kept money (baseline-scaled), then repriced money transferred in.
  const newTotals = {};
  Object.keys(baselineByAccount).forEach((idStr) => {
    const id = Number(idStr);
    // Nothing decomposed on this account -> nothing could be substituted from it, keep its baseline.
    newTotals[id] = (totalContrib[id] || 0) > 0 ? scaleFactor(id) * (keptContrib[id] || 0) : baselineByAccount[id];
  });
  // Category of every displayed account (in the clone), to place synthetic substitute rows next to
  // the line they replace.
  const categoryByAccount = {};
  [...(data.revenueAccountCategoryResults || []), ...(data.costAccountCategoryResults || [])]
    .forEach((cat) => (cat.accountResults || []).forEach((acc) => { categoryByAccount[acc.id] = cat; }));
  const syntheticRows = new Map(); // `${catId}|${substituteItemId}` -> synthetic account row

  transfers.forEach((t) => {
    const amount = scaleFactor(t.sourceAccount) * t.projected;
    if (t.destAccount != null) {
      if (newTotals[t.destAccount] == null) newTotals[t.destAccount] = baselineByAccount[t.destAccount] || 0;
      newTotals[t.destAccount] += amount;
      return;
    }
    const cat = categoryByAccount[t.sourceAccount];
    if (!cat) return; // source has no P&L row -> its scale factor is 0, nothing to move
    const key = `${cat.id}|${t.substituteItemId}`;
    let row = syntheticRows.get(key);
    if (!row) {
      const opt = subOptionsById?.[t.substituteItemId];
      const name = opt ? (opt.code ? `${opt.code} - ${opt.name}` : opt.name) : String(t.substituteItemId);
      row = { id: `sub-${t.substituteItemId}`, name, isSubstitute: true, total: 0 };
      syntheticRows.set(key, row);
      cat.accountResults = [...(cat.accountResults || []), row];
    }
    row.total += amount;
  });

  // Write the new per-account totals back and recompute the section totals.
  const writeBack = (cats) => {
    (cats || []).forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        if (newTotals[acc.id] != null) acc.total = newTotals[acc.id];
        else acc.total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };
  writeBack(data.revenueAccountCategoryResults);
  writeBack(data.costAccountCategoryResults);
  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Compute projected P&L for a change-composition scenario.
//
// Each edited bundle carries a full new recipe (list of { priceDefinitionId, multiplier, isFixed }).
// We reuse the price-definition split: the eventPrice of every measurement priced by a bundle is
// spread across its items using each item's share of the *original* bundle total, then rescaled by
// the change. Original contributions come from the true base recipe (bundle.items); projected
// contributions come from the edited recipe. Removed items project 0, added items project only.
// The two streams are then handed to applyAccountRatios, exactly like computePricePL.
function computeCompositionPL(basePLD, items, allMeasurements, allPriceBundles, subPriceById, subAccountById) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  if (!allMeasurements || !allPriceBundles) return data;

  // bundleId -> edited recipe (only bundles that were changed appear here).
  const overrides = {};
  (items || []).forEach((it) => { if (Array.isArray(it.compositionItems)) overrides[it.itemId] = it.compositionItems; });

  const bundleById = buildBundleById(allPriceBundles);

  const accountRatios = {};
  const addRatio = (accountId, original, projected) => {
    if (!accountId) return;
    if (!accountRatios[accountId]) accountRatios[accountId] = { original: 0, projected: 0 };
    accountRatios[accountId].original += original;
    accountRatios[accountId].projected += projected;
  };

  allMeasurements.forEach((m) => {
    const eventPrice = m.eventPrice || 0;
    const bundle = m.priceBundleId ? bundleById.get(m.priceBundleId) : null;
    if (!bundle || (bundle.items || []).length === 0) { addRatio(m.accountId, eventPrice, eventPrice); return; }

    const qty = getMeasurementQty(m);
    const originalBundleTotal = bundle.items.reduce((s, i) => s + i.price * i.multiplier * (i.isFixed ? 1 : qty), 0);
    if (originalBundleTotal <= 0) { addRatio(m.accountId, eventPrice, eventPrice); return; }

    // Original contributions from the true base recipe.
    bundle.items.forEach((i) => {
      const weight = i.isFixed ? 1 : qty;
      const origContrib = eventPrice * (i.price * i.multiplier * weight) / originalBundleTotal;
      addRatio(i.effectiveAccountId ?? bundle.accountId, origContrib, 0);
    });

    // Projected contributions from the edited recipe (base recipe when this bundle is unchanged).
    const recipe = overrides[bundle.id]
      || bundle.items.map((i) => ({ priceDefinitionId: i.priceDefinitionId, multiplier: i.multiplier, isFixed: !!i.isFixed }));
    const baseByPd = new Map(bundle.items.map((i) => [i.priceDefinitionId, i]));
    recipe.forEach((r) => {
      const baseItem = baseByPd.get(r.priceDefinitionId);
      const price = baseItem ? baseItem.price : (subPriceById?.[r.priceDefinitionId] ?? null);
      if (price == null) return; // unpriced added definition contributes nothing
      const acc = baseItem ? (baseItem.effectiveAccountId ?? bundle.accountId) : (subAccountById?.[r.priceDefinitionId] ?? bundle.accountId);
      const weight = r.isFixed ? 1 : qty;
      const projContrib = eventPrice * (price * Number(r.multiplier) * weight) / originalBundleTotal;
      addRatio(acc, 0, projContrib);
    });
  });

  return applyAccountRatios(data, accountRatios);
}

// ─── Allocate (production shift) simulation ──────────────────────────────────

// A line's volume unit: stopwatch details are measured in time (hours), every other
// control type in counts/values. Shifts are only allowed between details of the same
// unit type, so moving "N units" never mixes hours with pieces.
function unitTypeForMeasurement(m) {
  const isStopwatch =
    m.typeId === controlTypes.STOPWATCH ||
    (m.typeId === controlTypes.EXTERNALAPI && m.externalApiTypeId === externalApiTypes.STOPWATCH);
  return isStopwatch ? "time" : "count";
}

// Split a measurement's eventPrice into per-account euro contributions, using the same weighting the
// P&L uses (fixed items ×1, variable items ×qty). Per-measurement junction rows (price definitions
// applied to the detail) take priority over the bundle; each junction row / bundle item carries its
// own effective account. A measurement priced by neither (its whole eventPrice already sits on a
// bundle account) falls back to its own accountId. Returns [{ accountId, amount }].
//
// This matters for the allocate aggregate: a counter priced by price definitions (no bundle) has a
// null measurement accountId — its real accounts live only in the junction rows, so without this the
// unit would show €0 cost.
function decomposeMeasurementToAccounts(m, qty, bundleById, junctionByMeasurement) {
  const eventPrice = m.eventPrice || 0;
  if (eventPrice === 0) return [];

  const junction = junctionByMeasurement?.[m.id];
  if (junction && junction.length > 0) {
    const total = junction.reduce((s, j) => s + (j.unitPrice || 0) * (j.multiplier ?? 1) * (j.isFixed ? 1 : qty), 0);
    if (total > 0) {
      return junction.map((j) => {
        const weight = j.isFixed ? 1 : qty;
        return {
          accountId: j.effectiveAccountId,
          amount: eventPrice * ((j.unitPrice || 0) * (j.multiplier ?? 1) * weight) / total,
        };
      });
    }
  }

  const bundle = m.priceBundleId != null ? bundleById.get(m.priceBundleId) : null;
  if (bundle && (bundle.items || []).length > 0) {
    const originalBundleTotal = bundle.items.reduce((s, i) => s + i.price * i.multiplier * (i.isFixed ? 1 : qty), 0);
    if (originalBundleTotal > 0) {
      return bundle.items.map((item) => {
        const weight = item.isFixed ? 1 : qty;
        return {
          accountId: item.effectiveAccountId ?? bundle.accountId,
          amount: eventPrice * (item.price * item.multiplier * weight) / originalBundleTotal,
        };
      });
    }
  }
  return [{ accountId: m.accountId, amount: eventPrice }];
}

// One memoized pass over every measurement, aggregated per project (= per line). Only count/event
// measurements that carry a price bundle participate — a measurement with no PriceBundleId is out of
// scope for this simulation. For each line:
//   units[bundleId] = { bundleId, name, qty, accounts:{[accountId]: euros}, costTotal, revTotal } —
//     one editable production unit per count/event price bundle used on the line (keyed by
//     PriceBundleId, so custom per-measurement API bundles get their own row; name from
//     PriceBundleName). qty is the measured event count; the user edits it to simulate more/less
//     output, scaling that bundle's own measured cost and revenue proportionally. accounts drives the
//     per-account P&L delta; costTotal/revTotal (split via isRevenueAccount) drive the cost/revenue-
//     per-unit display.
// A line with no count/event price bundle still gets an (empty) entry so its card can show that it
// has no applicable bundles.
function buildLineAggregate(allMeasurements, allPriceBundles, isRevenueAccount, junctionByMeasurement) {
  const agg = {};
  if (!allMeasurements) return agg;
  const bundleById = buildBundleById(allPriceBundles);
  const isCostAccount = (accountId) => !(isRevenueAccount && isRevenueAccount(accountId));

  const ensureProject = (m) => {
    if (!agg[m.projectId]) {
      agg[m.projectId] = {
        projectId: m.projectId,
        projectName: m.projectName,
        resourceName: m.resourceName,
        units: {},
        // Implicit throughput model (Option A): total stopwatch euro contributions per account and
        // total count events across all bundles on the line. Used in computeAllocatePL to derive a
        // machine-time rate per produced unit (stopwatchAccounts[a] / totalCountQty).
        stopwatchAccounts: {},
        stopwatchCostTotal: 0,
        stopwatchRevTotal: 0,
        totalCountQty: 0,
      };
    }
    return agg[m.projectId];
  };

  allMeasurements.forEach((m) => {
    if (m.projectId == null) return;
    const p = ensureProject(m);

    if (unitTypeForMeasurement(m) === "time") {
      // Stopwatch: accumulate per-account euro contributions for the implicit throughput model.
      // Measurements without a bundle carry no priced cost here.
      if (m.priceBundleId == null) return;
      const qty = getMeasurementQty(m); // hours
      const contribs = decomposeMeasurementToAccounts(m, qty, bundleById, junctionByMeasurement);
      contribs.forEach((c) => {
        if (!c.accountId) return;
        p.stopwatchAccounts[c.accountId] = (p.stopwatchAccounts[c.accountId] || 0) + c.amount;
        if (isCostAccount(c.accountId)) p.stopwatchCostTotal += c.amount;
        else p.stopwatchRevTotal += c.amount;
      });
      return;
    }

    // Only count/event output priced by a bundle is in scope.
    if (m.priceBundleId == null) return;

    const qty = getMeasurementQty(m);
    const contribs = decomposeMeasurementToAccounts(m, qty, bundleById, junctionByMeasurement);
    const key = m.priceBundleId;
    if (!p.units[key]) {
      p.units[key] = {
        bundleId: key,
        name: m.priceBundleName || bundleById.get(key)?.name || `#${key}`,
        qty: 0,
        accounts: {},
        costTotal: 0,
        revTotal: 0,
      };
    }
    const u = p.units[key];
    u.qty += qty;
    p.totalCountQty += qty;
    contribs.forEach((c) => {
      if (!c.accountId) return;
      u.accounts[c.accountId] = (u.accounts[c.accountId] || 0) + c.amount;
      if (isCostAccount(c.accountId)) u.costTotal += c.amount;
      else u.revTotal += c.amount;
    });
  });

  return agg;
}

// Applies signed per-account euro deltas to a baseline P&L clone and recomputes section totals.
// Additive (not the ratio model), anchored to the baseline and floored at 0.
function applyAccountDeltas(data, deltas) {
  const apply = (cats) => {
    (cats || []).forEach((cat) => {
      let catTotal = 0;
      (cat.accountResults || []).forEach((acc) => {
        const baseTotal = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
        acc.total = Math.max(0, baseTotal + (deltas[acc.id] || 0));
        catTotal += acc.total;
      });
      cat.total = catTotal;
    });
  };
  apply(data.revenueAccountCategoryResults);
  apply(data.costAccountCategoryResults);
  data.revenueTotal = (data.revenueAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.costTotal = (data.costAccountCategoryResults || []).reduce((a, c) => a + c.total, 0);
  data.totalProfit = data.revenueTotal - data.costTotal;
  return data;
}

// Compute projected P&L for an allocate-type scenario. Each item overrides one price bundle's quantity
// on one line. Two delta components are applied:
//
// 1. Bundle deltas — each bundle's own measured cost+revenue scales by (N − Q) / Q:
//      delta(a) += unit.accounts[a] × (N − Q) / Q
//
// 2. Stopwatch deltas — implicit throughput model (Option A): machine-time cost scales with the net
//    change in total count events on the line, using the line's average rate (total stopwatch euros /
//    total count events). Assumes all bundles on the line share the same machine-time rate per event:
//      delta(a) += line.stopwatchAccounts[a] / line.totalCountQty × netCountDelta
function computeAllocatePL(basePLD, items, lineAgg) {
  if (!basePLD) return null;
  const data = deepClone(basePLD);
  if (!lineAgg) return data;

  const deltas = {};
  const addDelta = (accountId, v) => { if (!accountId) return; deltas[accountId] = (deltas[accountId] || 0) + v; };

  // Phase 1: bundle deltas.
  (items || []).forEach((it) => {
    const a = it.allocationData;
    if (!a || a.projectId == null || a.priceBundleId == null) return;
    const unit = lineAgg[a.projectId]?.units?.[a.priceBundleId];
    if (!unit || unit.qty <= 0) return;
    const factor = (Number(a.newQuantity) - unit.qty) / unit.qty;
    if (!isFinite(factor) || factor === 0) return;
    Object.entries(unit.accounts).forEach(([accId, amount]) => addDelta(Number(accId), amount * factor));
  });

  // Phase 2: stopwatch deltas — net count change on each line × machine-time rate per event.
  const netCountDeltaByProject = {};
  (items || []).forEach((it) => {
    const a = it.allocationData;
    if (!a || a.projectId == null || a.priceBundleId == null) return;
    const unit = lineAgg[a.projectId]?.units?.[a.priceBundleId];
    if (!unit || unit.qty <= 0) return;
    const delta = Number(a.newQuantity) - unit.qty;
    if (!isFinite(delta)) return;
    netCountDeltaByProject[a.projectId] = (netCountDeltaByProject[a.projectId] || 0) + delta;
  });
  Object.entries(netCountDeltaByProject).forEach(([projectId, totalDelta]) => {
    if (totalDelta === 0) return;
    const line = lineAgg[Number(projectId)];
    if (!line?.totalCountQty) return;
    Object.entries(line.stopwatchAccounts || {}).forEach(([accId, totalEuros]) => {
      addDelta(Number(accId), totalEuros * totalDelta / line.totalCountQty);
    });
  });

  return applyAccountDeltas(data, deltas);
}

function computeScenarioPL(basePLD, scenario, allMeasurements, allPriceBundles, junctionByMeasurement, subPriceById, subAccountById, lineAgg, subOptionsById) {
  if (!scenario) return null;
  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return computeAccountsPL(basePLD, scenario.items || []);
  }
  if (scenario.scenarioType === simulationScenarioTypes.SUBSTITUTE) {
    return computeSubstitutePL(basePLD, scenario.items || [], allMeasurements, allPriceBundles, junctionByMeasurement, subPriceById, subAccountById, subOptionsById);
  }
  if (scenario.scenarioType === simulationScenarioTypes.COMPOSITION) {
    return computeCompositionPL(basePLD, scenario.items || [], allMeasurements, allPriceBundles, subPriceById, subAccountById);
  }
  if (scenario.scenarioType === simulationScenarioTypes.ALLOCATE) {
    return computeAllocatePL(basePLD, scenario.items || [], lineAgg);
  }
  return computePricePL(basePLD, scenario.items || [], allMeasurements, allPriceBundles);
}

function getBaselinePL(profitLossReportData) {
  if (!profitLossReportData || Array.isArray(profitLossReportData) || !profitLossReportData.revenueAccountCategoryResults) return null;
  const data = deepClone(profitLossReportData);

  const normalise = (cats) => {
    (cats || []).forEach((cat) => {
      const catT = typeof cat.total === "object" ? Object.values(cat.total)[0] ?? 0 : cat.total ?? 0;
      cat.total = catT;
      (cat.accountResults || []).forEach((acc) => {
        acc.total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
      });
    });
  };
  normalise(data.revenueAccountCategoryResults);
  normalise(data.costAccountCategoryResults);

  data.revenueTotal = typeof data.revenueTotal === "object" ? Object.values(data.revenueTotal)[0] ?? 0 : data.revenueTotal ?? 0;
  data.costTotal = typeof data.costTotal === "object" ? Object.values(data.costTotal)[0] ?? 0 : data.costTotal ?? 0;
  data.totalProfit = typeof data.totalProfit === "object" ? Object.values(data.totalProfit)[0] ?? 0 : data.totalProfit ?? 0;
  return data;
}

// Build the projection object (format consumed by ProfitLossReport / InsightsTab)
// from the current paramRows of a scenario. For substitute scenarios the active substitution
// items and the substitute-price lookup are supplied via subContext.
function buildProjection(scenario, paramRows, subContext) {
  if (!scenario || scenario.isBaseline) return false;

  if (scenario.scenarioType === simulationScenarioTypes.COMPOSITION) {
    // Payload is a map bundleId -> edited recipe. Each recipe item carries its resolved price and
    // account so the cross-tab consumers (calculateProjection / ProfitLossReport) need no lookups:
    // base items resolve from the bundle, added definitions from the substitute-price/account maps.
    const bundleById = buildBundleById(subContext?.allPriceBundles || []);
    const composition = {};
    (subContext?.compositionItems || []).forEach((it) => {
      if (!Array.isArray(it.compositionItems)) return;
      const bundle = bundleById.get(it.itemId);
      if (!bundle) return;
      const baseByPd = new Map((bundle.items || []).map((i) => [i.priceDefinitionId, i]));
      composition[it.itemId] = it.compositionItems.map((r) => {
        const baseItem = baseByPd.get(r.priceDefinitionId);
        const price = baseItem ? baseItem.price : (subContext?.subPriceById?.[r.priceDefinitionId] ?? null);
        const accountId = baseItem
          ? (baseItem.effectiveAccountId ?? bundle.accountId)
          : (subContext?.subAccountById?.[r.priceDefinitionId] ?? bundle.accountId);
        const name = baseItem ? (baseItem.priceDefinitionName ?? null) : (subContext?.subNameById?.[r.priceDefinitionId] ?? null);
        return { priceDefinitionId: r.priceDefinitionId, multiplier: Number(r.multiplier), isFixed: !!r.isFixed, price, accountId, name };
      });
    });
    if (Object.keys(composition).length === 0) return false;
    return { composition };
  }

  if (scenario.scenarioType === simulationScenarioTypes.SUBSTITUTE) {
    const subs = (subContext?.items || [])
      .filter((i) => i.substituteItemId != null)
      .map((i) => ({
        originalPriceDefinitionId: i.itemId,
        substitutePriceDefinitionId: i.substituteItemId,
        newPrice: subContext?.subPriceById?.[i.substituteItemId] ?? null,
        substituteName: subContext?.subNameById?.[i.substituteItemId] ?? null,
        scope: i.bundleScope || null,
      }))
      .filter((s) => s.newPrice != null);
    if (subs.length === 0) return false;
    return { substitutions: subs };
  }

  if (scenario.scenarioType === simulationScenarioTypes.ALLOCATE) {
    const items = (scenario.items || []).filter((i) => i.allocationData != null);
    if (items.length === 0) return false;

    // Per-line cost deltas so InsightsTab can show projected loss by line. Each edited bundle scales
    // its own measured cost by the quantity change factor.
    const lineAgg = subContext?.lineAgg || {};
    const lineCostDeltas = {};
    items.forEach((item) => {
      const a = item.allocationData;
      if (!a || a.priceBundleId == null) return;
      const line = lineAgg[a.projectId];
      const unit = line?.units?.[a.priceBundleId];
      if (!unit || unit.qty <= 0) return;
      const factor = (Number(a.newQuantity) - unit.qty) / unit.qty;
      if (!isFinite(factor) || factor === 0) return;
      const name = line.resourceName || line.projectName || `#${a.projectId}`;
      lineCostDeltas[name] = (lineCostDeltas[name] || 0) + unit.costTotal * factor;
    });

    return { allocation: true, lineCostDeltas: Object.keys(lineCostDeltas).length > 0 ? lineCostDeltas : null };
  }

  const changed = paramRows.filter((p) => Math.abs(p.editVal - p.currentValue) > 0.001);
  if (changed.length === 0) return false;

  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    return {
      accounts: changed.map((p) => ({
        id: p.id,
        name: p.name,
        isRevenue: p.isRevenue,
        percentage: p.currentValue !== 0 ? (p.editVal / p.currentValue) * 100 : 100,
      })),
    };
  }
  return {
    priceDefinitions: changed.map((p) => ({
      priceDefinitionId: p.id,
      priceDefinitionName: p.name,
      currentPrice: p.currentValue,
      newPrice: p.editVal,
    })),
  };
}

function computeScenarioChanges(scenario, profitLossReportData, allPriceBundles, subContext) {
  if (!scenario || scenario.isBaseline || !scenario.items?.length) return { type: null, changes: [] };

  if (scenario.scenarioType === simulationScenarioTypes.SUBSTITUTE) {
    const defsById = subContext?.defsById || {};
    const subOptionsById = subContext?.subOptionsById || {};
    const changes = [];
    scenario.items.forEach((item) => {
      if (item.substituteItemId == null) return;
      const orig = defsById[item.itemId];
      const sub = subOptionsById[item.substituteItemId];
      if (!orig || !sub) return;
      changes.push({
        name: orig.name,
        fromName: orig.name,
        toName: sub.name,
        from: orig.currentValue ?? 0,
        to: sub.currentValue ?? 0,
      });
    });
    return { type: "substitute", changes };
  }

  if (scenario.scenarioType === simulationScenarioTypes.COMPOSITION) {
    // Per changed bundle: the per-unit recipe total (Σ price·multiplier) before vs after.
    const subOptionsById = subContext?.subOptionsById || {};
    const bundleById = buildBundleById(allPriceBundles);
    const priceOf = (pdId, baseItem) => (baseItem ? baseItem.price : (subOptionsById[pdId]?.currentValue ?? null)) ?? 0;
    const changes = [];
    scenario.items.forEach((item) => {
      if (!Array.isArray(item.compositionItems)) return;
      const bundle = bundleById.get(item.itemId);
      if (!bundle) return;
      const baseByPd = new Map((bundle.items || []).map((i) => [i.priceDefinitionId, i]));
      const baseTotal = (bundle.items || []).reduce((s, i) => s + i.price * i.multiplier, 0);
      const newTotal = item.compositionItems.reduce((s, r) => s + priceOf(r.priceDefinitionId, baseByPd.get(r.priceDefinitionId)) * Number(r.multiplier), 0);
      if (Math.abs(newTotal - baseTotal) > 0.001) {
        changes.push({ name: bundle.name, from: baseTotal, to: newTotal });
      }
    });
    return { type: "composition", changes };
  }

  if (scenario.scenarioType === simulationScenarioTypes.ALLOCATE) {
    const lineAgg = subContext?.lineAgg || {};
    const nameOf = (pid) => lineAgg[pid]?.resourceName || lineAgg[pid]?.projectName || `#${pid}`;
    const changes = [];
    scenario.items.forEach((item) => {
      const a = item.allocationData;
      if (!a || a.projectId == null || a.priceBundleId == null) return;
      const unit = lineAgg[a.projectId]?.units?.[a.priceBundleId];
      changes.push({
        name: `${nameOf(a.projectId)} · ${unit?.name ?? `#${a.priceBundleId}`}`,
        from: unit?.qty ?? 0,
        to: Number(a.newQuantity),
      });
    });
    return { type: "allocate", changes };
  }

  if (scenario.scenarioType === simulationScenarioTypes.ACCOUNTS) {
    const accountMap = {};
    const traverse = (cats) => {
      (cats || []).forEach((cat) => {
        (cat.accountResults || []).forEach((acc) => {
          const total = typeof acc.total === "object" ? Object.values(acc.total)[0] ?? 0 : acc.total ?? 0;
          accountMap[acc.id] = { name: acc.name, currentValue: total };
        });
      });
    };
    if (profitLossReportData && !Array.isArray(profitLossReportData)) {
      traverse(profitLossReportData.revenueAccountCategoryResults);
      traverse(profitLossReportData.costAccountCategoryResults);
    }
    const changes = [];
    scenario.items.forEach((item) => {
      const acc = accountMap[item.itemId];
      if (!acc) return;
      if (Math.abs(item.newValue - acc.currentValue) > 0.001) {
        changes.push({ name: acc.name, from: acc.currentValue, to: item.newValue });
      }
    });
    return { type: "accounts", changes };
  }

  // PRICE_DEFINITIONS
  const pdMap = {};
  (allPriceBundles || []).forEach((company) => {
    (company.priceBundles || []).forEach((bundle) => {
      (bundle.items || []).forEach((item) => {
        if (!pdMap[item.priceDefinitionId]) {
          pdMap[item.priceDefinitionId] = { name: item.priceDefinitionName, currentValue: item.price };
        }
      });
    });
  });
  const changes = [];
  scenario.items.forEach((item) => {
    const pd = pdMap[item.itemId];
    if (!pd) return;
    if (Math.abs(item.newValue - pd.currentValue) > 0.001) {
      changes.push({ name: pd.name, from: pd.currentValue, to: item.newValue });
    }
  });
  return { type: "priceDefinitions", changes };
}

// Returns a scenario's saved value for a param; falls back to the param's current value
function getScenarioParamVal(scenario, paramId, fallback) {
  if (!scenario) return fallback;
  const item = (scenario.items || []).find((i) => i.itemId === paramId);
  return item != null ? item.newValue : fallback;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function NewScenarioModal({ open, onConfirm, onCancel }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [type, setType] = useState(simulationScenarioTypes.ACCOUNTS);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setType(simulationScenarioTypes.ACCOUNTS);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  return (
    <Dialog open={open} maxWidth={false} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle className={classes.dialog__title}>{t('projectionsTab.newScenario')}</DialogTitle>
        <button className={classes.dialog__closeButton} onClick={onCancel}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          <p className={classes.dialog__sub}>{t('projectionsTab.newScenarioSubtitle')}</p>
          <div className={classes.inputContainer}>
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>{t('common.name')}</label>
            </div>
            <Input
              ref={inputRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('projectionsTab.namePlaceholder')}
              onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) onConfirm(name.trim(), type); }}
              fullwidth
            />
          </div>
          <div className={classes.inputContainer} style={{ marginTop: 16 }}>
            <div className={classes.labelContainer}>
              <label className={classes.labelText}>{t('projectionsTab.parameterType')}</label>
            </div>
            <div className={classes.typeRow}>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.ACCOUNTS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.ACCOUNTS)}
              >
                {t('projectionsTab.accounts')}
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.PRICE_DEFINITIONS ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.PRICE_DEFINITIONS)}
              >
                {t('projectionsTab.priceDefinitions')}
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.SUBSTITUTE ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.SUBSTITUTE)}
              >
                {t('projectionsTab.substitute')}
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.COMPOSITION ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.COMPOSITION)}
              >
                {t('projectionsTab.composition')}
              </button>
              <button
                className={`${classes.typeBtn} ${type === simulationScenarioTypes.ALLOCATE ? classes.typeBtn_active : ""}`}
                onClick={() => setType(simulationScenarioTypes.ALLOCATE)}
              >
                {t('projectionsTab.allocate')}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between", paddingLeft: "16px", paddingRight: "16px", paddingBottom: "16px" }}>
        <Button variant="secondary" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button disabled={!name.trim()} onClick={() => onConfirm(name.trim(), type)}>{t('common.create')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function ParamRow({ param, editValue, onChange, onReset, disabled, currency, decimals = 0, uom, showCategory = true, comparisonCols = [], showEditCol = true }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const currentVal = param.currentValue ?? 0;
  const scenarioVal = editValue !== undefined ? editValue : currentVal;
  const isChanged = Math.abs(scenarioVal - currentVal) > 0.001;
  const pct = currentVal !== 0 ? ((scenarioVal - currentVal) / Math.abs(currentVal)) * 100 : 0;
  const pctDisplay = pct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const isRevenue = param.isRevenue === true || param.group === "Revenue";
  const isCost = param.isRevenue === false || param.group === "Cost";
  const chipVariant = !isChanged ? "default" : isRevenue ? (pct > 0 ? "success" : "danger") : (pct > 0 ? "danger" : "success");

  const formatNum = (n) => n.toLocaleString("nl-BE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  const [displayVal, setDisplayVal] = useState(() => formatNum(scenarioVal));
  // Track the last value we propagated up so we can distinguish external prop changes from our own
  const lastPropagatedRef = useRef(scenarioVal);
  const inputRef = useRef(null);
  const cursorPosRef = useRef(null);

  useEffect(() => {
    if (Math.abs(scenarioVal - lastPropagatedRef.current) > 0.0001) {
      lastPropagatedRef.current = scenarioVal;
      setDisplayVal(formatNum(scenarioVal));
    }
  }, [scenarioVal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore cursor after React re-renders the input with the new formatted value
  useLayoutEffect(() => {
    if (cursorPosRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
      cursorPosRef.current = null;
    }
  });

  const handleChange = (e) => {
    const raw = e.target.value;
    const cursorPos = e.target.selectionStart;

    const formatted = processPriceInput(raw, 2);
    if (formatted === null) return; // invalid char — ignore, keep current display

    // Remap cursor: count significant chars (digits + comma) before cursor in raw,
    // then find the same count in the formatted string (dots are separators and shift positions).
    const sigBefore = (raw.slice(0, cursorPos).match(/[\d,]/g) || []).length;
    let newCursorPos = formatted.length;
    if (sigBefore === 0) {
      newCursorPos = 0;
    } else {
      let sigCount = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/[\d,]/.test(formatted[i])) {
          if (++sigCount === sigBefore) { newCursorPos = i + 1; break; }
        }
      }
    }
    cursorPosRef.current = newCursorPos;

    setDisplayVal(formatted);
    const parsed = parsePrice(formatted);
    const numVal = parsed !== null ? parsed : 0;
    lastPropagatedRef.current = numVal;
    onChange(param.id, numVal);
  };

  const handleBlur = () => {
    // Reformat with thousands separators on blur
    const parsed = parsePrice(displayVal);
    if (parsed !== null) setDisplayVal(formatNum(parsed));
  };

  return (
    <tr className={classes.paramRow}>
      <td className={classes.paramCell}>
        <div className={classes.param__name}>{param.name}</div>
        {showCategory && param.categoryName && <div className={classes.param__cat}>{param.categoryName}</div>}
      </td>
      <td className={`${classes.paramCell} ${classes.paramCell_mono}`}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span>{fmt(currentVal, currency, decimals)}</span>
          {uom && <Chip variant="default" size="extraSmall">{uom}</Chip>}
        </div>
      </td>
      {comparisonCols.map((col, ci) => {
        const colDiff = col.value - col.baseline;
        const colChanged = Math.abs(colDiff) > 0.001;
        const colPct = col.baseline !== 0 ? (colDiff / Math.abs(col.baseline)) * 100 : 0;
        const colPctDisplay = (colPct > 0 ? "+" : "") + colPct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
        const colChipVariant = col.isRevenue ? (colPct > 0 ? "success" : "danger") : (colPct > 0 ? "danger" : "success");
        return (
          <td key={ci} className={`${classes.paramCell} ${classes.paramCell_mono}`} style={{ textAlign: "right" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <span>{colChanged ? <Chip variant={colChipVariant} size="extraSmall">{colPctDisplay}</Chip> : null}</span>
              <span style={colChanged ? { color: colChipVariant === "success" ? "var(--color-text-success)" : "var(--color-text-danger)", fontWeight: 500 } : undefined}>{fmt(col.value, currency, decimals)}</span>
            </div>
          </td>
        );
      })}
      {showEditCol && (
        <td className={classes.paramCell}>
          <div className={classes.param__inputWrap}>
            <span className={classes.param__unit}>{currency?.symbol ?? "€"}</span>
            <input
              ref={inputRef}
              className={`${classes.param__input} ${isChanged ? classes.param__input_changed : ""}`}
              value={displayVal}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {isChanged && (
              <button
                className={classes.param__resetBtn}
                onClick={() => onReset && onReset(param.id)}
                title={t('projectionsTab.resetToOriginal')}
                tabIndex={-1}
              >
                <RotateCcw size={14} />
              </button>
            )}
          </div>
        </td>
      )}
      <td className={classes.paramCell}>
        <Chip
          variant={chipVariant}
          size="extraSmall"
        >
          {!isChanged ? "—" : (pct > 0 ? "+" : "") + pctDisplay + "%"}
        </Chip>
      </td>
    </tr>
  );
}

// Human-readable summary of a substitution's scope ("All occurrences", "2 bundles, Direct", ...).
function scopeSummaryText(scope, t) {
  if (!scope) return t('projectionsTab.allOccurrences');
  const parts = [];
  const n = scope.bundleIds?.length || 0;
  if (n) parts.push(t('projectionsTab.bundleCount', { count: n }));
  if (scope.direct) parts.push(t('projectionsTab.directShort'));
  return parts.length ? parts.join(", ") : t('projectionsTab.noneSelected');
}

const SUB_GRID = "1.5fr 1.9fr minmax(80px, auto) 72px";

// One substitutable definition: original (left), the chosen substitution + scope summary (middle),
// price delta (right), dedicated action buttons column (far right).
function SubstitutionRow({ def, value, onEdit, onClear, substituteOptions, currency, readOnly }) {
  const { t } = useTranslation();
  const substituteItemId = value?.substituteItemId ?? null;
  const substitute = substituteItemId != null ? substituteOptions.find((o) => o.id === substituteItemId) : null;
  const subPrice = substitute?.currentValue ?? null;
  const unpriced = substitute != null && subPrice == null;
  const uomMismatch = substitute && substitute.uom && def.uom && substitute.uom !== def.uom;

  const delta = substitute && subPrice != null ? subPrice - (def.currentValue ?? 0) : null;
  const deltaPct = delta != null && def.currentValue ? (delta / Math.abs(def.currentValue)) * 100 : null;
  const isRevenue = def.group === "Revenue";
  const deltaVariant = delta == null || Math.abs(delta) < 0.001
    ? "default"
    : isRevenue ? (delta > 0 ? "success" : "danger") : (delta > 0 ? "danger" : "success");

  return (
    <div style={{
      display: "grid", gridTemplateColumns: SUB_GRID, gap: 14, alignItems: "center",
      padding: "10px 0", borderBottom: "1px solid var(--color-border-light)",
    }}>
      {/* Original */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {def.code && <span style={{ fontFamily: "'DM Mono', monospace", opacity: 0.7, marginRight: 6 }}>{def.code}</span>}
          {def.name}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: "var(--color-text-secondary)" }}>{fmt(def.currentValue ?? 0, currency, 2)}</span>
          {def.uom && <Chip variant="default" size="extraSmall">{def.uom}</Chip>}
        </div>
      </div>

      {/* Substitution / action */}
      <div style={{ minWidth: 0 }}>
        {substitute ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <ArrowRight size={14} style={{ flexShrink: 0, color: "var(--color-text-secondary)" }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {substitute.code && <span style={{ fontFamily: "'DM Mono', monospace", opacity: 0.7, marginRight: 6 }}>{substitute.code}</span>}
                  {substitute.name}
                </span>
                {unpriced && <Chip variant="warning" size="extraSmall">{t('projectionsTab.noPrice')}</Chip>}
                {uomMismatch && <Chip variant="warning" size="extraSmall">{substitute.uom}</Chip>}
              </div>
              <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {scopeSummaryText(value?.scope ?? null, t)}
              </div>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="small" onClick={() => onEdit(def)} disabled={readOnly}>
            {t('projectionsTab.chooseSubstitute')}
          </Button>
        )}
      </div>

      {/* Delta */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
        {delta != null && Math.abs(delta) >= 0.001 && (
          <>
            <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: deltaVariant === "success" ? "var(--color-text-success)" : deltaVariant === "danger" ? "var(--color-text-danger)" : undefined }}>
              {delta > 0 ? "+" : ""}{fmt(delta, currency, 2)}
            </span>
            {deltaPct != null && (
              <Chip variant={deltaVariant} size="extraSmall">
                {(deltaPct > 0 ? "+" : "") + deltaPct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
              </Chip>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
        {substitute && !readOnly && (
          <>
            <IconButton ariaLabel={t('projectionsTab.editSubstitution')} variant="ghost" size="small" onClick={() => onEdit(def)}>
              <Pencil size={14} />
            </IconButton>
            <IconButton ariaLabel={t('projectionsTab.removeSubstitution')} variant="ghost" size="small" onClick={() => onClear(def)}>
              <X size={14} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}

// Editor body for a substitute scenario: a list of SubstitutionRows ordered by definition code.
// Resolves each row's value as unsaved edits (editSubs) layered over the scenario's saved value,
// and owns the SubstitutionDialog used to configure a row.
function SubstitutionEditorBody({ defs, activeScenario, editSubs, onChange, substituteOptions, readOnly, currency }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [dialogDef, setDialogDef] = useState(null);

  const resolveValue = (def) => {
    if (Object.prototype.hasOwnProperty.call(editSubs, def.id)) return editSubs[def.id];
    const saved = (activeScenario.items || []).find((i) => i.itemId === def.id);
    return saved && saved.substituteItemId != null
      ? { substituteItemId: saved.substituteItemId, scope: saved.bundleScope || null }
      : null;
  };

  if (!defs || defs.length === 0) {
    return <div className={classes.paramEmpty}>{t('projectionsTab.noSubstitutableDefinitions')}</div>;
  }

  return (
    <div style={{ padding: "8px 16px 4px" }}>
      <div style={{
        display: "grid", gridTemplateColumns: SUB_GRID, gap: 14, alignItems: "center",
        padding: "0 0 6px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px",
        color: "var(--color-text-secondary)", fontFamily: "'DM Mono', monospace",
      }}>
        <span>{t('projectionsTab.original')}</span>
        <span>{t('projectionsTab.substitute')}</span>
        <span style={{ textAlign: "right" }}>{t('projectionsTab.delta')}</span>
        <span />
      </div>

      {defs.map((def) => (
        <SubstitutionRow
          key={def.id}
          def={def}
          value={resolveValue(def)}
          onEdit={setDialogDef}
          onClear={(d) => onChange(d.id, null)}
          substituteOptions={substituteOptions}
          currency={currency}
          readOnly={readOnly}
        />
      ))}

      <SubstitutionDialog
        open={!!dialogDef}
        def={dialogDef}
        value={dialogDef ? resolveValue(dialogDef) : null}
        substituteOptions={substituteOptions}
        currency={currency}
        onClose={() => setDialogDef(null)}
        onConfirm={(defId, next) => { onChange(defId, next); setDialogDef(null); }}
      />
    </div>
  );
}

function ScenarioChangesTooltip({ changes, type, currency }) {
  const { t } = useTranslation();
  const decimals = type === "accounts" ? 0 : 2;
  const header = type === "priceDefinitions"
    ? t('projectionsTab.priceDefinitionChanges')
    : type === "substitute"
    ? t('projectionsTab.substitutionChanges')
    : type === "composition"
    ? t('projectionsTab.compositionChanges')
    : type === "allocate"
    ? t('projectionsTab.allocateChanges')
    : t('projectionsTab.accountChanges');
  return (
    <div style={{ minWidth: 200, maxWidth: 340 }}>
      <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.6px" }}>
        {header}
      </div>
      {changes.map((c, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            marginBottom: i < changes.length - 1 ? 5 : 0,
          }}
        >
          {type === "substitute" ? (
            <>
              <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.fromName}&nbsp;→&nbsp;{c.toName}</span>
              <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", opacity: 0.85 }}>
                {fmt(c.from, currency, decimals)}&nbsp;→&nbsp;{fmt(c.to, currency, decimals)}
              </span>
            </>
          ) : type === "allocate" ? (
            <>
              <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.name}</span>
              <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", opacity: 0.85 }}>
                {Math.round(c.from).toLocaleString()}&nbsp;→&nbsp;{Math.round(c.to).toLocaleString()}
              </span>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12 }}>{c.name}</span>
              <span style={{ fontSize: 11, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", opacity: 0.85 }}>
                {fmt(c.from, currency, decimals)}&nbsp;→&nbsp;{fmt(c.to, currency, decimals)}
              </span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function PLTable({ baseline, orderedSimCols, activeScenarioId, currency, onReorderCols, bestScenarioId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [dragSrcIdx, setDragSrcIdx] = useState(null);
  const [dragTargetIdx, setDragTargetIdx] = useState(null);

  if (!baseline) return null;

  const showDelta = orderedSimCols.length > 0;

  // Helpers to safely extract totals from Dapper's object-or-scalar shape
  const catTotal = (cat) =>
    cat ? (typeof cat.total === "object" ? Object.values(cat.total)[0] ?? 0 : cat.total ?? 0) : 0;
  const getCats = (pl, isRevenue) =>
    (isRevenue ? pl?.revenueAccountCategoryResults : pl?.costAccountCategoryResults) || [];

  const handleDragStart = (ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) return;
    setDragSrcIdx(ci);
  };
  const handleDragOver = (e, ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) return;
    e.preventDefault();
    setDragTargetIdx(ci);
  };
  const handleDragEnd = () => { setDragSrcIdx(null); setDragTargetIdx(null); };
  const handleDrop = (ci) => {
    if (orderedSimCols[ci]?.id === activeScenarioId) { setDragSrcIdx(null); setDragTargetIdx(null); return; }
    if (dragSrcIdx !== null && dragSrcIdx !== ci) {
      const newIds = orderedSimCols.map((s) => s.id);
      const [moved] = newIds.splice(dragSrcIdx, 1);
      newIds.splice(ci, 0, moved);
      onReorderCols(newIds);
    }
    setDragSrcIdx(null);
    setDragTargetIdx(null);
  };

  const deltaCell = (baseVal, bestVal, isRevenue = true, isCategory = false) => {
    const diff = bestVal - baseVal;
    if (Math.abs(diff) < 1) return <td className={`${classes.plCell} ${classes.plCell_delta}`}>—</td>;
    const isImprovement = isRevenue ? diff > 0 : diff < 0;
    const cls = isImprovement ? classes.plVal_pos : classes.plVal_neg;
    return (
      <td
        className={`${classes.plCell} ${classes.plCell_delta} ${cls}`}
        style={isCategory ? { fontWeight: 600 } : undefined}
      >
        {diff > 0 ? "+" : ""}{fmt(diff, currency, 2)}
      </td>
    );
  };

  const valCell = (val, baseVal, isSection, isTotal, isRevenue = true, isCategory = false) => {
    const changed = Math.abs(val - baseVal) > 1;
    const betterClass = isRevenue ? classes.plVal_pos : classes.plVal_neg;
    const worseClass = isRevenue ? classes.plVal_neg : classes.plVal_pos;
    const cls = changed ? (val > baseVal ? betterClass : worseClass) : "";
    const pct = changed && baseVal !== 0 ? ((val - baseVal) / Math.abs(baseVal)) * 100 : 0;
    const pctDisplay = (pct > 0 ? "+" : "") + pct.toLocaleString("nl-BE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + "%";
    const chipVariant = isRevenue ? (pct > 0 ? "success" : "danger") : (pct > 0 ? "danger" : "success");
    return (
      <td
        className={`${classes.plCell} ${isSection ? classes.plCell_section : ""} ${isTotal ? classes.plCell_total : ""} ${isCategory ? classes.plCell_categoryAmount : ""} ${cls}`}
        style={isCategory ? { fontWeight: 600 } : undefined}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          <span>{changed && baseVal !== 0 && <Chip variant={chipVariant} size="extraSmall">{pctDisplay}</Chip>}</span>
          {fmt(val, currency, 2)}
        </div>
      </td>
    );
  };

  const renderSection = (label, isRevenue) => {
    const origBaseCats = (isRevenue ? baseline.revenueAccountCategoryResults : baseline.costAccountCategoryResults) || [];
    const baseCats = origBaseCats.slice().sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    const colSpan = 2 + orderedSimCols.length + (showDelta ? 1 : 0);
    const bestSimCats = bestSimCol ? getCats(bestSimCol._pl ?? baseline, isRevenue) : null;

    const rows = [
      <tr key={label} className={classes.plSectionRow}>
        <td className={`${classes.plCell} ${classes.plCell_section} ${isRevenue ? classes.plCell_section_revenue : classes.plCell_section_cost}`} colSpan={colSpan}>
          {label}
        </td>
      </tr>,
    ];

    baseCats.forEach((baseCat) => {
      const baseT = catTotal(baseCat);

      // Pre-compute each sim column's category total (needed for relative % and valCell colouring)
      const baseCatIdx = origBaseCats.indexOf(baseCat);
      const simCatTs = orderedSimCols.map((simCol) => {
        const simCats = getCats(simCol._pl ?? baseline, isRevenue);
        const simCat = (baseCatIdx >= 0 ? simCats[baseCatIdx] : null) ?? simCats.find((c) => c.id != null ? c.id === baseCat.id : c.name === baseCat.name) ?? baseCat;
        return catTotal(simCat);
      });

      // Delta Best: look up the same category in the best-profit scenario
      const bestSimCat = bestSimCats
        ? ((baseCatIdx >= 0 ? bestSimCats[baseCatIdx] : null) ?? bestSimCats.find((c) => c.id != null ? c.id === baseCat.id : c.name === baseCat.name) ?? baseCat)
        : baseCat;
      const bestCatT = catTotal(bestSimCat);

      rows.push(
        <tr key={baseCat.id} className={classes.plCategoryRow}>
          <td className={`${classes.plCell} ${classes.plCell_category}`}>{baseCat.name}</td>
          {valCell(baseT, baseT, false, false, isRevenue, true)}
          {orderedSimCols.map((simCol, ci) =>
            valCell(simCatTs[ci], baseT, false, false, isRevenue, true)
          )}
          {showDelta && deltaCell(baseT, bestCatT, isRevenue, true)}
        </tr>
      );

      (baseCat.accountResults || []).slice().sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")).forEach((baseAcc) => {
        const baseAccT = catTotal(baseAcc);

        const baseAccIdx = (baseCat.accountResults || []).indexOf(baseAcc);
        const simAccTs = orderedSimCols.map((simCol) => {
          const simCats = getCats(simCol._pl ?? baseline, isRevenue);
          const simCat = (baseCatIdx >= 0 ? simCats[baseCatIdx] : null) ?? simCats.find((c) => c.id != null ? c.id === baseCat.id : c.name === baseCat.name) ?? baseCat;
          const simAccs = simCat.accountResults || [];
          const simAcc = (baseAccIdx >= 0 ? simAccs[baseAccIdx] : null) ?? simAccs.find((a) => a.id != null ? a.id === baseAcc.id : a.name === baseAcc.name) ?? baseAcc;
          return catTotal(simAcc);
        });

        // Delta Best: look up account in the best-profit scenario's category
        const bestSimAccs = bestSimCat.accountResults || [];
        const bestSimAcc = (baseAccIdx >= 0 ? bestSimAccs[baseAccIdx] : null) ?? bestSimAccs.find((a) => a.id != null ? a.id === baseAcc.id : a.name === baseAcc.name) ?? baseAcc;
        const bestAccT = catTotal(bestSimAcc);

        rows.push(
          <tr key={`${baseCat.id}-${baseAcc.id}`} className={classes.plRow}>
            <td className={`${classes.plCell} ${classes.plCell_account}`}><span className={classes.plCell_account_inner}><ArrowRight className={classes.plCell_account_icon} size={12} />{baseAcc.name}</span></td>
            {valCell(baseAccT, baseAccT, false, false, isRevenue)}
            {orderedSimCols.map((simCol, ci) =>
              valCell(simAccTs[ci], baseAccT, false, false, isRevenue)
            )}
            {showDelta && deltaCell(baseAccT, bestAccT, isRevenue)}
          </tr>
        );
      });

      // Rows that only exist in a simulation (a substitute without a P&L account of its own): baseline 0.
      const simCatsForBase = orderedSimCols.map((simCol) => {
        const simCats = getCats(simCol._pl ?? baseline, isRevenue);
        return (baseCatIdx >= 0 ? simCats[baseCatIdx] : null) ?? simCats.find((c) => c.id != null ? c.id === baseCat.id : c.name === baseCat.name) ?? null;
      });
      const baseAccIds = new Set((baseCat.accountResults || []).map((a) => a.id));
      const simOnlyAccs = [];
      simCatsForBase.forEach((simCat) => (simCat?.accountResults || []).forEach((a) => {
        if (!baseAccIds.has(a.id) && !simOnlyAccs.some((x) => x.id === a.id)) simOnlyAccs.push(a);
      }));
      simOnlyAccs.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")).forEach((acc) => {
        const findIn = (cat) => (cat?.accountResults || []).find((a) => a.id === acc.id);
        rows.push(
          <tr key={`${baseCat.id}-${acc.id}`} className={classes.plRow}>
            <td className={`${classes.plCell} ${classes.plCell_account}`}>
              <span className={classes.plCell_account_inner}>
                <ArrowRight className={classes.plCell_account_icon} size={12} />{acc.name}
                {acc.isSubstitute && <Chip variant="simulation" size="extraSmall" style={{ marginLeft: 6 }}>{t('projectionsTab.substituteRowTag')}</Chip>}
              </span>
            </td>
            {valCell(0, 0, false, false, isRevenue)}
            {simCatsForBase.map((simCat) => valCell(catTotal(findIn(simCat)), 0, false, false, isRevenue))}
            {showDelta && deltaCell(0, catTotal(findIn(bestSimCat)), isRevenue)}
          </tr>
        );
      });
    });

    return rows;
  };

  const baseRevT = baseline.revenueTotal ?? 0;
  const baseCostT = baseline.costTotal ?? 0;
  const baseProfit = baseline.totalProfit ?? 0;
  const simRevTs = orderedSimCols.map((s) => s._pl?.revenueTotal ?? 0);
  const simCostTs = orderedSimCols.map((s) => s._pl?.costTotal ?? 0);
  const simProfits = orderedSimCols.map((s) => s._pl?.totalProfit ?? 0);

  // The single scenario with the best (highest) total profit — used for all Delta Best cells
  // so every row reflects the same scenario rather than mixing which scenario "wins" per row.
  const bestSimCol = orderedSimCols.length > 0
    ? orderedSimCols.reduce((best, s) =>
        (s._pl?.totalProfit ?? -Infinity) > (best._pl?.totalProfit ?? -Infinity) ? s : best
      )
    : null;

  return (
    <table className={`${classes.plTable} no-stripes`} style={{ tableLayout: "fixed" }}>
      <colgroup>
        <col />
        <col style={{ width: SIM_COL_W }} />
        {orderedSimCols.map((s) => <col key={s.id} style={{ width: SIM_COL_W }} />)}
        {showDelta && <col />}
      </colgroup>
      <thead>
        <tr>
          <th className={classes.plTh} style={{ textAlign: "left" }}>{t('projectionsTab.plComponent')}</th>
          <th className={`${classes.plTh} ${classes.plTh_baseline}`}>{t('projectionsTab.baseline')}</th>
          {orderedSimCols.map((s, ci) => {
            const isActive = activeScenarioId === s.id;
            const isBest = bestScenarioId === s.id;
            return (
            <th
              key={s.id}
              className={`${classes.plTh} ${isActive ? classes.plTh_scenario : (isBest ? classes.plTh_best : classes.plTh_compare)} ${dragTargetIdx === ci && dragSrcIdx !== ci ? classes.plTh_dragOver : ""}`}
              draggable={!isActive}
              onDragStart={() => handleDragStart(ci)}
              onDragOver={(e) => handleDragOver(e, ci)}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(ci)}
              style={{ cursor: isActive ? "default" : "grab" }}
            >
              <span style={{ display: "flex", alignItems: "center", justifyContent: isActive ? "flex-end" : "space-between", overflow: "hidden" }}>
                {!isActive && <GripVertical size={13} style={{ opacity: 0.45, flexShrink: 0 }} />}
                <span style={{ display: "flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
                  {isBest && <Trophy size={12} style={{ flexShrink: 0, color: isActive ? "#d97706" : "rgba(255,255,255,0.95)" }} />}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                  {s._changes?.changes?.length > 0 && (
                    <Tooltip
                      title={<ScenarioChangesTooltip changes={s._changes.changes} type={s._changes.type} currency={currency} />}
                      placement="bottom-end"
                      arrow
                    >
                      <span
                        style={{ display: "inline-flex", cursor: "help", flexShrink: 0 }}
                        onDragStart={(e) => e.stopPropagation()}
                      >
                        <HelpCircle size={13} style={{ opacity: 0.65 }} />
                      </span>
                    </Tooltip>
                  )}
                </span>
              </span>
            </th>
          );
          })}
          {showDelta && <th className={`${classes.plTh} ${classes.plTh_delta}`}>{t('projectionsTab.deltaBest')}</th>}
        </tr>
      </thead>
      <tbody>
        {renderSection(t('projectionsTab.revenue'), true)}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>{t('projectionsTab.totalRevenue')}</strong></td>
          {valCell(baseRevT, baseRevT, false, false, true)}
          {orderedSimCols.map((s, ci) => valCell(simRevTs[ci], baseRevT, false, false, true))}
          {showDelta && deltaCell(baseRevT, bestSimCol?._pl?.revenueTotal ?? baseRevT, true)}
        </tr>

        {renderSection(t('projectionsTab.costs'), false)}
        <tr className={classes.plSubtotalRow}>
          <td className={classes.plCell}><strong>{t('projectionsTab.totalCosts')}</strong></td>
          {valCell(baseCostT, baseCostT, false, false, false)}
          {orderedSimCols.map((s, ci) => valCell(simCostTs[ci], baseCostT, false, false, false))}
          {showDelta && deltaCell(baseCostT, bestSimCol?._pl?.costTotal ?? baseCostT, false)}
        </tr>

        <tr className={classes.plTotalRow}>
          <td className={`${classes.plCell} ${classes.plCell_total}`}>{t('projectionsTab.grossProfit')}</td>
          {valCell(baseProfit, baseProfit, false, true)}
          {orderedSimCols.map((s, ci) => valCell(simProfits[ci], baseProfit, false, true))}
          {showDelta && deltaCell(baseProfit, bestSimCol?._pl?.totalProfit ?? baseProfit, true)}
        </tr>
      </tbody>
    </table>
  );
}

// ─── Scenario kebab menu ─────────────────────────────────────────────────────

const kebabMenuPanel = {
  position: "absolute",
  top: "100%",
  right: 0,
  zIndex: 1000,
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  minWidth: 120,
  overflow: "hidden",
};

const kebabMenuDestructiveItem = {
  display: "block",
  width: "100%",
  padding: "6px 12px",
  textAlign: "left",
  fontSize: 13,
  fontFamily: "Lato, sans-serif",
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "#ef4444",
};

function ScenarioKebabMenu({ onDelete }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <IconButton
        size="extraSmall"
        variant="ghost"
        ariaLabel={t("common.actions")}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <MoreVertical size={14} />
      </IconButton>
      {open && (
        <div style={kebabMenuPanel}>
          <button
            style={kebabMenuDestructiveItem}
            onClick={() => { setOpen(false); onDelete(); }}
          >
            {t("common.delete")}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ProjectionsTab({
  graphId,
  profitLossReportData,
  allPriceBundles,
  allMeasurements,
  currency,
  login,
  activeProjection,
  handleSetActiveProjection,
  // Lifted state — survives tab switches
  selectedScenarioId,
  onSelectedScenarioChange,
  appliedScenarioId,
  onAppliedScenarioChange,
  readOnly = false,
  // Registration-point names in the graph configuration's order (drives the allocate card order).
  projectOrder = [],
}) {
  const classes = useStyles();
  const { t } = useTranslation();

  // Saved scenarios loaded from backend (no baseline here)
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);

  const activeScenarioId = selectedScenarioId;
  const setActiveScenarioId = onSelectedScenarioChange;

  const [compareIds, setCompareIds] = useState([]);
  const [simColOrder, setSimColOrder] = useState([]);

  // Unsaved param edits { itemId: newValue }
  const [editParams, setEditParams] = useState({});

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [baselineParamType, setBaselineParamType] = useState(simulationScenarioTypes.PRICE_DEFINITIONS);

  // Substitute simulation data: the substitute picker (definitions with their live current price)
  // and the per-measurement junction breakdown (measurements whose price definitions override the bundle).
  const [substituteOptions, setSubstituteOptions] = useState([]);
  const [junctionByMeasurement, setJunctionByMeasurement] = useState({});
  // Unsaved substitution edits: { [originalPriceDefinitionId]: { substituteItemId, scope } | null }
  const [editSubs, setEditSubs] = useState({});
  // Unsaved composition edits: { [bundleId]: recipe[] } (recipe = full edited item list for the bundle)
  const [editComposition, setEditComposition] = useState({});
  // Undo/redo history of editComposition snapshots; cleared on scenario switch/save.
  const [compositionHistory, setCompositionHistory] = useState({ past: [], future: [] });
  // Last composition edit's coalesce key + time, so keystrokes in one field form one undo step.
  const lastCompositionEditRef = useRef({ key: null, at: 0 });
  // Unsaved allocate edits: { qtyByProject } or null (null = fall back to saved scenario)
  const [editAllocation, setEditAllocation] = useState(null);
  const [deleteScenarioId, setDeleteScenarioId] = useState(null);
  const [scenarioSearch, setScenarioSearch] = useState("");
  const [scenarioTypeFilters, setScenarioTypeFilters] = useState([]);

  // Sync: if activeProjection was cleared externally, clear the applied tracker
  useEffect(() => {
    if (!activeProjection) onAppliedScenarioChange(null);
  }, [activeProjection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the substitute picker (all price definitions with their current price).
  useEffect(() => {
    const companyId = allMeasurements?.[0]?.companyId ?? null;
    if (!companyId) return;
    dataService.getPriceDefinitionsByCompanyId(companyId)
      .then((data) => {
        setSubstituteOptions((Array.isArray(data) ? data : []).map((d) => ({
          id: d.id,
          code: d.code ?? null,
          name: d.name,
          currentValue: d.currentPrice ?? null,
          uom: d.unitOfMeasurement ?? null,
          defaultAccountId: d.defaultAccountId ?? null,
        })));
      })
      .catch(() => setSubstituteOptions([]));
  }, [allMeasurements?.[0]?.companyId]);

  // Load the per-measurement junction breakdown for the graph. Scoped server-side by the graph's
  // projects + date range, so no measurement-id list is sent. Grouped by measurement id.
  useEffect(() => {
    if (!graphId) { setJunctionByMeasurement({}); return; }
    dataService.getGraphJunctionItemsForGraph(graphId)
      .then((rows) => {
        const grouped = {};
        (Array.isArray(rows) ? rows : []).forEach((r) => {
          const mid = r.projectDetailMeasurementId;
          if (!grouped[mid]) grouped[mid] = [];
          grouped[mid].push(r);
        });
        setJunctionByMeasurement(grouped);
      })
      .catch(() => setJunctionByMeasurement({}));
  }, [graphId]);

  // Fast lookups for substitute price / name / option by id.
  const subPriceById = React.useMemo(() => {
    const map = {};
    substituteOptions.forEach((o) => { map[o.id] = o.currentValue; });
    return map;
  }, [substituteOptions]);
  const subNameById = React.useMemo(() => {
    const map = {};
    substituteOptions.forEach((o) => { map[o.id] = o.name ?? o.code ?? null; });
    return map;
  }, [substituteOptions]);
  const subOptionsById = React.useMemo(() => {
    const map = {};
    substituteOptions.forEach((o) => { map[o.id] = o; });
    return map;
  }, [substituteOptions]);
  // Price-definition code by id, sourced from the picker (which returns every definition).
  const codeById = React.useMemo(() => {
    const map = {};
    substituteOptions.forEach((o) => { if (o.code) map[o.id] = o.code; });
    return map;
  }, [substituteOptions]);
  // Substitute's own default account by id (null when it has none); the swapped amount lands on it in
  // the P&L, or on a synthetic substitute row when it has none (see computeSubstitutePL).
  const subAccountById = React.useMemo(() => {
    const map = {};
    substituteOptions.forEach((o) => { map[o.id] = o.defaultAccountId ?? null; });
    return map;
  }, [substituteOptions]);

  // Flat junction items list (for building the substitutable-definition union).
  const junctionItemsFlat = React.useMemo(
    () => Object.values(junctionByMeasurement).flat(),
    [junctionByMeasurement]
  );

  const baselinePL = getBaselinePL(profitLossReportData);

  // Revenue-account classifier for the allocate editor (so a shift's profit impact separates the
  // revenue and cost sides). Cost is everything not in a revenue category.
  const isRevenueAccount = React.useMemo(() => {
    const rev = new Set();
    (profitLossReportData?.revenueAccountCategoryResults || []).forEach((cat) =>
      (cat.accountResults || []).forEach((a) => rev.add(a.id))
    );
    return (id) => rev.has(id);
  }, [profitLossReportData]);

  // Per-line (per-project) aggregate for allocate scenarios: marginal cost/value per account,
  // per-bundle event volume, and per-contributor cost. One pass over every measurement; only reruns
  // when the data changes, so the shift editor recomputes deltas from this small summary rather than
  // rescanning the raw rows.
  const lineAgg = React.useMemo(
    () => buildLineAggregate(allMeasurements, allPriceBundles, isRevenueAccount, junctionByMeasurement),
    [allMeasurements, allPriceBundles, isRevenueAccount, junctionByMeasurement]
  );

  const paramsForType = (type) => {
    if (type === simulationScenarioTypes.ACCOUNTS) return extractAccounts(profitLossReportData);
    if (type === simulationScenarioTypes.SUBSTITUTE) return extractSubstitutableDefinitions(allPriceBundles, junctionItemsFlat, profitLossReportData, codeById);
    if (type === simulationScenarioTypes.COMPOSITION) return []; // composition edits bundles, not a flat param list
    if (type === simulationScenarioTypes.ALLOCATE) return []; // allocate edits production shifts, not a flat param list
    return extractPriceDefinitions(allPriceBundles, profitLossReportData);
  };

  // Flat list of every price bundle used by the graph, carrying its company name (for the
  // composition editor). Ordered by company then bundle name.
  const compositionBundles = React.useMemo(() => {
    const list = [];
    (allPriceBundles || []).forEach((company) => {
      (company.priceBundles || []).forEach((b) => {
        list.push({ ...b, companyName: b.companyName || company.name });
      });
    });
    list.sort((a, b) => (a.companyName ?? "").localeCompare(b.companyName ?? "") || (a.name ?? "").localeCompare(b.name ?? ""));
    return list;
  }, [allPriceBundles]);

  const allParams = (() => {
    if (activeScenarioId !== "baseline" && savedScenarios.find((s) => s.id === activeScenarioId)) {
      const s = savedScenarios.find((sc) => sc.id === activeScenarioId);
      return paramsForType(s.scenarioType);
    }
    // Baseline active — derive params from first compare scenario if comparing, otherwise use baselineParamType
    const firstCompare = savedScenarios.find((s) => compareIds.includes(s.id));
    if (firstCompare) return paramsForType(firstCompare.scenarioType);
    return paramsForType(baselineParamType);
  })();

  // Substitutable definitions keyed by id, and the shared context used by buildProjection /
  // computeScenarioChanges for substitute scenarios.
  const substitutableDefs = React.useMemo(
    () => extractSubstitutableDefinitions(allPriceBundles, junctionItemsFlat, profitLossReportData, codeById),
    [allPriceBundles, junctionItemsFlat, profitLossReportData, codeById]
  );
  const defsById = React.useMemo(() => {
    const map = {};
    substitutableDefs.forEach((d) => { map[d.id] = d; });
    return map;
  }, [substitutableDefs]);

  // Build scenario list: baseline + saved
  const scenarioList = [
    {
      id: "baseline",
      name: t('projectionsTab.currentBaseline'),
      isBaseline: true,
      scenarioType: simulationScenarioTypes.ACCOUNTS,
      items: [],
      _pl: baselinePL,
    },
    ...[...savedScenarios].sort((a, b) => a.name.localeCompare(b.name)).map((s) => ({
      ...s,
      _pl: computeScenarioPL(baselinePL, s, allMeasurements, allPriceBundles, junctionByMeasurement, subPriceById, subAccountById, lineAgg, subOptionsById),
      _changes: computeScenarioChanges(s, profitLossReportData, allPriceBundles, { defsById, subOptionsById, lineAgg }),
    })),
  ];

  const filteredScenarioList = scenarioList.filter((s) => {
    if (s.isBaseline) return true;
    const q = scenarioSearch.trim().toLowerCase();
    const matchesSearch = !q || s.name.toLowerCase().includes(q);
    const matchesType = scenarioTypeFilters.length === 0 || scenarioTypeFilters.includes(s.scenarioType);
    return matchesSearch && matchesType;
  });

  // Compute active scenario's "preview" P&L (incorporates unsaved edits)
  const activeScenario = scenarioList.find((s) => s.id === activeScenarioId) ?? scenarioList[0];
  const isActiveSubstitute = !activeScenario.isBaseline && activeScenario.scenarioType === simulationScenarioTypes.SUBSTITUTE;
  const isActiveComposition = !activeScenario.isBaseline && activeScenario.scenarioType === simulationScenarioTypes.COMPOSITION;
  const isActiveAllocate = !activeScenario.isBaseline && activeScenario.scenarioType === simulationScenarioTypes.ALLOCATE;

  // Resolve the active composition scenario's items = only bundles whose recipe differs from the base,
  // each as { itemId: bundleId, compositionItems: recipe[] }. Unsaved edits (editComposition) layered
  // over the scenario's saved items.
  const activeCompositionItems = isActiveComposition
    ? compositionBundles
        .map((bundle) => {
          const recipe = resolveCompositionRecipe(bundle, editComposition, activeScenario.items);
          return recipeDiffersFromBase(recipe, bundle)
            ? { itemId: bundle.id, newValue: null, compositionItems: recipe }
            : null;
        })
        .filter(Boolean)
    : [];

  // Resolve the active substitute scenario's items = unsaved edits (editSubs) layered over saved items.
  const activeSubItems = isActiveSubstitute
    ? allParams
        .map((def) => {
          if (Object.prototype.hasOwnProperty.call(editSubs, def.id)) {
            const v = editSubs[def.id];
            return v && v.substituteItemId != null
              ? { itemId: def.id, substituteItemId: v.substituteItemId, bundleScope: v.scope || null }
              : null;
          }
          const saved = activeScenario.items.find((i) => i.itemId === def.id);
          return saved && saved.substituteItemId != null
            ? { itemId: def.id, substituteItemId: saved.substituteItemId, bundleScope: saved.bundleScope || null }
            : null;
        })
        .filter(Boolean)
    : [];

  // Resolve the active allocate scenario's state (unsaved edits over saved shifts) and its items.
  const activeAllocationState = isActiveAllocate ? resolveAllocationState(activeScenario, editAllocation, lineAgg) : null;
  const activeAllocationItems = isActiveAllocate ? allocationStateToItems(activeAllocationState, lineAgg) : [];

  const activeScenarioWithEdits = {
    ...activeScenario,
    items: isActiveComposition
      ? activeCompositionItems
      : isActiveAllocate
      ? activeAllocationItems
      : isActiveSubstitute
      ? activeSubItems
      : allParams.map((p) => ({
          itemId: p.id,
          newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
        })),
  };
  const activePL = activeScenario.isBaseline
    ? baselinePL
    : computeScenarioPL(baselinePL, activeScenarioWithEdits, allMeasurements, allPriceBundles, junctionByMeasurement, subPriceById, subAccountById, lineAgg, subOptionsById);

  const compareScenarios = compareIds
    .map((id) => scenarioList.find((s) => s.id === id))
    .filter(Boolean);

  // Ordered sim columns shared by both P&L table and param comparison columns
  const showSimulation = !activeScenario?.isBaseline;
  const allSimCols = showSimulation
    ? [{ ...activeScenario, _pl: activePL }, ...compareScenarios]
    : [...compareScenarios];
  const allSimMap = new Map(allSimCols.map((s) => [s.id, s]));
  let orderedSimCols = [
    ...simColOrder.filter((id) => allSimMap.has(id)).map((id) => allSimMap.get(id)),
    ...allSimCols.filter((s) => !simColOrder.includes(s.id)),
  ];
  // Pin the active/editing scenario to the last position (just before Delta)
  if (showSimulation) {
    const activeIdx = orderedSimCols.findIndex((s) => s.id === activeScenario.id);
    if (activeIdx !== -1 && activeIdx !== orderedSimCols.length - 1) {
      const [activeCol] = orderedSimCols.splice(activeIdx, 1);
      orderedSimCols.push(activeCol);
    }
  }

  // Best scenario: highest simulated profit among loaded columns (only meaningful when 2+)
  const bestScenarioId = orderedSimCols.length > 1
    ? orderedSimCols.reduce((best, s) =>
        (s._pl?.totalProfit ?? 0) > (best._pl?.totalProfit ?? 0) ? s : best
      ).id
    : null;

  // Compare-only columns: when baseline is active all orderedSimCols are compare cols; otherwise exclude the active editing scenario
  const isComparing = compareIds.length > 0;
  // Effective param type: for baseline, use baselineParamType (or the first compare scenario's type when comparing)
  const effectiveParamType = activeScenario.isBaseline
    ? (isComparing ? (savedScenarios.find((s) => compareIds.includes(s.id))?.scenarioType ?? simulationScenarioTypes.ACCOUNTS) : baselineParamType)
    : activeScenario.scenarioType;
  const showScenarioCol = showSimulation || isComparing;
  const compareSimCols = isComparing
    ? (activeScenario?.isBaseline ? orderedSimCols : orderedSimCols.filter((s) => s.id !== activeScenario.id))
    : [];
  const paramColCount = isComparing ? 2 + orderedSimCols.length + 1 : (showScenarioCol ? 4 : 3);

  // ── data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!graphId) return;
    setLoadingScenarios(true);
    dataService
      .getGraphSimulationScenarios(graphId)
      .then((data) => {
        setSavedScenarios(Array.isArray(data) ? data.map((s) => ({
          ...s,
          scenarioType: s.scenarioType,
          items: (s.items || []).map((i) => ({
            itemId: i.itemId,
            newValue: i.newValue != null ? Number(i.newValue) : null,
            substituteItemId: i.substituteItemId ?? null,
            bundleScope: i.bundleScope ?? null,
            compositionItems: Array.isArray(i.compositionItems)
              ? i.compositionItems.map((c) => ({
                  priceDefinitionId: c.priceDefinitionId,
                  multiplier: Number(c.multiplier),
                  isFixed: !!c.isFixed,
                }))
              : null,
            allocationData: i.allocationData && i.allocationData.projectId != null && i.allocationData.priceBundleId
              ? {
                  projectId: i.allocationData.projectId,
                  priceBundleId: i.allocationData.priceBundleId,
                  newQuantity: Number(i.allocationData.newQuantity),
                }
              : null,
          })),
        })) : []);
      })
      .catch(() => setSavedScenarios([]))
      .finally(() => setLoadingScenarios(false));
  }, [graphId]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleSelectScenario = (id) => {
    setActiveScenarioId(id);
    setEditParams({});
    setEditSubs({});
    setEditComposition({});
    clearCompositionHistory();
    setEditAllocation(null);
  };

  const handleUpdateParam = (paramId, value) => {
    setEditParams((prev) => ({ ...prev, [paramId]: value }));
  };

  // Substitution edit: value = { substituteItemId, scope } or null to clear.
  const handleUpdateSub = (defId, value) => {
    setEditSubs((prev) => ({ ...prev, [defId]: value }));
  };

  const clearCompositionHistory = () => {
    setCompositionHistory({ past: [], future: [] });
    lastCompositionEditRef.current = { key: null, at: 0 };
  };

  // Replace editComposition, recording the previous state for undo. Edits sharing a coalesceKey in
  // quick succession (typing in one multiplier field) collapse into a single undo step.
  const commitComposition = (next, coalesceKey = null) => {
    const now = Date.now();
    const last = lastCompositionEditRef.current;
    const coalesce = coalesceKey != null && last.key === coalesceKey && now - last.at < 1500;
    lastCompositionEditRef.current = { key: coalesceKey, at: now };
    if (!coalesce) {
      const snapshot = editComposition;
      setCompositionHistory((h) => ({ past: [...h.past, snapshot].slice(-100), future: [] }));
    }
    setEditComposition(next);
  };

  const handleUndoComposition = () => {
    const { past, future } = compositionHistory;
    if (past.length === 0) return;
    lastCompositionEditRef.current = { key: null, at: 0 };
    setCompositionHistory({ past: past.slice(0, -1), future: [editComposition, ...future] });
    setEditComposition(past[past.length - 1]);
  };

  const handleRedoComposition = () => {
    const { past, future } = compositionHistory;
    if (future.length === 0) return;
    lastCompositionEditRef.current = { key: null, at: 0 };
    setCompositionHistory({ past: [...past, editComposition], future: future.slice(1) });
    setEditComposition(future[0]);
  };

  // Composition edit: recipe = the bundle's full new item list.
  const handleUpdateComposition = (bundleId, recipe, coalesceKey = null) => {
    commitComposition({ ...editComposition, [bundleId]: recipe }, coalesceKey ? `${bundleId}:${coalesceKey}` : null);
  };

  // Revert one bundle inside an editComposition map: drop its unsaved edit, or, if the scenario has a
  // saved override for it, seed the edit with the base recipe so it reverts to the original.
  const revertBundleIn = (next, bundleId) => {
    const bundle = compositionBundles.find((b) => b.id === bundleId);
    const hasSavedOverride = (activeScenario.items || []).some((i) => i.itemId === bundleId && Array.isArray(i.compositionItems));
    if (hasSavedOverride && bundle) {
      next[bundleId] = baseCompositionRecipe(bundle);
    } else {
      delete next[bundleId];
    }
  };

  const handleResetBundleComposition = (bundleId) => {
    const next = { ...editComposition };
    revertBundleIn(next, bundleId);
    commitComposition(next);
  };

  // Revert every modified bundle back to its original recipe (undoable).
  const handleResetAllComposition = () => {
    const next = { ...editComposition };
    activeCompositionItems.forEach((it) => revertBundleIn(next, it.itemId));
    commitComposition(next);
  };

  // Footer reset drops unsaved edits; for composition this is recorded so it can be undone.
  const handleResetEdits = () => {
    setEditParams({});
    setEditSubs({});
    if (Object.keys(editComposition).length > 0) commitComposition({});
    setEditAllocation(null);
  };

  const handleResetParam = (paramId) => {
    const param = allParams.find((p) => p.id === paramId);
    if (!param) return;
    setEditParams((prev) => ({ ...prev, [paramId]: param.currentValue }));
  };

  const handleSave = async () => {
    if (activeScenario.isBaseline) return;
    setSaving(true);
    try {
      const items = isActiveComposition
        ? activeCompositionItems.map((it) => ({
            itemId: it.itemId,
            newValue: null,
            compositionItems: it.compositionItems,
          }))
        : isActiveAllocate
        ? activeAllocationItems.map((it) => ({
            itemId: it.itemId,
            newValue: null,
            allocationData: it.allocationData,
          }))
        : isActiveSubstitute
        ? activeSubItems.map((it) => ({
            itemId: it.itemId,
            newValue: null,
            substituteItemId: it.substituteItemId,
            bundleScope: it.bundleScope || null,
          }))
        : allParams.map((p) => ({
            itemId: p.id,
            newValue: editParams[p.id] !== undefined ? editParams[p.id] : (activeScenario.items.find((i) => i.itemId === p.id)?.newValue ?? p.currentValue),
          }));
      const result = await dataService.updateGraphSimulationScenario(
        activeScenario.id,
        activeScenario.name,
        items,
        login?.userid ?? 0
      );
      if (result?.success !== false) {
        setSavedScenarios((prev) =>
          prev.map((s) =>
            s.id === activeScenario.id ? { ...s, items } : s
          )
        );
        setEditParams({});
        setEditSubs({});
        setEditComposition({});
        clearCompositionHistory();
        setEditAllocation(null);
        toast.success(t('projectionsTab.scenarioSaved'), { autoClose: 2000 });
      } else {
        toast.error(result.message || t('projectionsTab.saveFailed'), { autoClose: 3000 });
      }
    } catch {
      toast.error(t('projectionsTab.saveFailed'), { autoClose: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleNewScenario = async (name, type) => {
    setModalOpen(false);
    try {
      const result = await dataService.createGraphSimulationScenario(
        graphId,
        name,
        type,
        login?.userid ?? 0
      );
      if (result?.success !== false && result?.scenarioId) {
        const newScenario = { id: result.scenarioId, name, scenarioType: type, items: [] };
        setSavedScenarios((prev) => [...prev, newScenario]);
        setActiveScenarioId(result.scenarioId);
        setEditParams({});
        setEditSubs({});
        setEditComposition({});
        clearCompositionHistory();
        setEditAllocation(null);
      } else {
        toast.error(result?.message || t('projectionsTab.createScenarioFailed'), { autoClose: 3000 });
      }
    } catch {
      toast.error(t('projectionsTab.createScenarioFailed'), { autoClose: 3000 });
    }
  };

  const handleDeleteScenario = async (id) => {
    try {
      await dataService.deleteGraphSimulationScenario(id, login?.userid ?? 0);
      setSavedScenarios((prev) => prev.filter((s) => s.id !== id));
      setCompareIds((prev) => prev.filter((x) => x !== id));
      if (activeScenarioId === id) setActiveScenarioId("baseline");
    } catch {
      toast.error(t('projectionsTab.deleteScenarioFailed'), { autoClose: 3000 });
    }
  };

  const handleApply = () => {
    const projection = buildProjection(activeScenario, paramRows, {
      items: activeSubItems,
      subPriceById,
      subNameById,
      subAccountById,
      compositionItems: activeCompositionItems,
      allPriceBundles,
      lineAgg,
    });
    if (!projection) {
      toast.info(t('projectionsTab.noChangesToApply'), { autoClose: 3000 });
      return;
    }
    handleSetActiveProjection({ ...projection, name: activeScenario.name, _pl: activePL ?? null });
    onAppliedScenarioChange(activeScenarioId);
  };

  const handleClearProjection = () => {
    handleSetActiveProjection(null);
    onAppliedScenarioChange(null);
  };

  const handleToggleCompare = (id) => {
    if (id === activeScenarioId) return;
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) return [...prev.slice(1), id];
      return [...prev, id];
    });
  };

  const hasUnsavedEdits = Object.keys(editParams).length > 0 || Object.keys(editSubs).length > 0 || Object.keys(editComposition).length > 0 || editAllocation != null;

  // ── param list for active scenario ────────────────────────────────────────

  const paramRows = allParams.map((p) => {
    const savedItem = activeScenario.items.find((i) => i.itemId === p.id);
    const editVal = editParams[p.id] !== undefined ? editParams[p.id] : (savedItem?.newValue ?? p.currentValue);
    return { ...p, editVal };
  });

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className={classes.root}>
      {/* SIDEBAR */}
      <div className={classes.sidebar}>
        <div className={classes.sidebar__titleRow}>
          <div className={classes.sidebar__title}>{t('projectionsTab.scenarios')}</div>
          {!readOnly && (
            <Button variant="primary" size="extraSmall" startIcon={<Plus />} onClick={() => setModalOpen(true)}>
              {t('projectionsTab.newScenario')}
            </Button>
          )}
        </div>

        <div className={classes.sidebar__filterArea}>
          <SearchInput
            value={scenarioSearch}
            onChange={(e) => setScenarioSearch(e.target.value)}
            onClear={() => setScenarioSearch("")}
            placeholder={t('projectionsTab.searchScenarios')}
            size="small"
            fullwidth
          />
          <Select
            value={scenarioTypeFilters}
            onChange={setScenarioTypeFilters}
            multiple
            placeholder={t('projectionsTab.filterByType')}
            size="small"
            fullwidth
          >
            <SelectTrigger />
            <SelectContent>
              {[simulationScenarioTypes.ACCOUNTS, simulationScenarioTypes.PRICE_DEFINITIONS, simulationScenarioTypes.SUBSTITUTE, simulationScenarioTypes.COMPOSITION, simulationScenarioTypes.ALLOCATE].map((type) => (
                <SelectItem key={type} value={type}>
                  {getScenarioTypeLabel(type, t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={classes.sidebar__list}>
          {loadingScenarios ? (
            <div className={classes.sidebar__loading}>{t('common.loading')}</div>
          ) : (
            filteredScenarioList.map((s) => {
            const impact = s.isBaseline ? null : ((s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0));
            const isActive = s.id === activeScenarioId;
            const isComparing = compareIds.includes(s.id);
            return (
              <div
                key={s.id}
                className={`${classes.scenarioCard} ${isActive ? classes.scenarioCard_active : ""} ${isComparing && !isActive ? classes.scenarioCard_comparing : ""}`}
                onClick={() => !isComparing && handleSelectScenario(s.id)}
                style={isComparing && !isActive ? { cursor: "default" } : undefined}
              >
                <div className={classes.scenarioCard__nameRow}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 }}>
                    <span className={classes.scenarioCard__name}>{s.name}</span>
                    {!s.isBaseline && (
                      <div>
                        <Chip variant="simulation" size="extraSmall">
                          {getScenarioTypeLabel(s.scenarioType, t)}
                        </Chip>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 4, alignItems: "flex-start", flexShrink: 0 }}>
                    {isComparing && !isActive && (
                      <button
                        className={classes.scenarioCard__editBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCompareIds((prev) => prev.filter((x) => x !== s.id));
                          handleSelectScenario(s.id);
                        }}
                        title={t('projectionsTab.editScenario')}
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {!s.isBaseline && bestScenarioId === s.id && (
                      <span className={classes.scenarioCard__bestBadge}>
                        <Trophy size={9} />{t('projectionsTab.best')}
                      </span>
                    )}
                    {!s.isBaseline && appliedScenarioId === s.id && (
                      <span className={classes.scenarioCard__appliedBadge}>{t('projectionsTab.activeBadge')}</span>
                    )}
                    {s.isBaseline && <span className={classes.scenarioCard__tag}>{t('projectionsTab.baselineTag')}</span>}
                    {!s.isBaseline && !readOnly && (
                      <ScenarioKebabMenu onDelete={() => setDeleteScenarioId(s.id)} />
                    )}
                  </div>
                </div>
                <div className={classes.scenarioCard__summary}>
                  {s.isBaseline
                    ? `${t('projectionsTab.grossProfitPrefix')} ${fmt(baselinePL?.totalProfit ?? 0, currency)}`
                    : impact != null
                    ? `${t('projectionsTab.impactPrefix')} ${impact >= 0 ? "+" : ""}${fmt(impact, currency)}`
                    : "—"}
                </div>
                {!s.isBaseline && (
                  <div className={classes.scenarioCard__actions} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="extraSmall"
                      variant={isComparing ? "outlined" : "secondary"}
                      onClick={() => handleToggleCompare(s.id)}
                      startIcon={isComparing ? <Check /> : null}
                      disabled={isActive}
                    >
                      {t('projectionsTab.compare')}
                    </Button>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>

        {compareIds.length > 0 && (
          <div className={classes.compareBar}>
            <div className={classes.compareBar__title}>{t('projectionsTab.comparing')}</div>
            {compareIds.map((id) => {
              const s = scenarioList.find((x) => x.id === id);
              if (!s) return null;
              const impact = (s._pl?.totalProfit ?? 0) - (baselinePL?.totalProfit ?? 0);
              return (
                <div key={id} className={classes.compareBar__item}>
                  <span>{s.name}</span>
                  <span style={{ color: impact >= 0 ? "#4ade80" : "#f87171" }}>
                    {impact >= 0 ? "+" : ""}{fmt(impact, currency)}
                  </span>
                </div>
              );
            })}
            <button className={classes.compareBar__clear} onClick={() => setCompareIds([])}>{t('projectionsTab.clear')}</button>
          </div>
        )}
      </div>

      {/* MAIN */}
      <div className={classes.main}>
        {/* ACTIVE PROJECTION INDICATOR */}
        {activeProjection && appliedScenarioId && (
          <div className={classes.projectionBanner}>
            <div className={classes.projectionBanner__left}>
              <CheckCircle2 size={15} className={classes.projectionBanner__icon} />
              <span>
                {t('projectionsTab.simulationActive')}
                {scenarioList.find((s) => s.id === appliedScenarioId) && (
                  <> — <strong>{scenarioList.find((s) => s.id === appliedScenarioId)?.name}</strong></>
                )}
              </span>
            </div>
            <Button variant="secondary" size="small" onClick={handleClearProjection}>{t('projectionsTab.clear')}</Button>
          </div>
        )}
        {/* PARAMETER EDITOR */}
        <div className={classes.editorCard}>
          <div className={classes.editorCard__head}>
            <div>
              <div className={classes.editorCard__title}>
                {t('projectionsTab.parameters')} — {activeScenario.name}
                {!activeScenario.isBaseline && (
                  <span className={classes.typeChip}>
                    {activeScenario.scenarioType === simulationScenarioTypes.ACCOUNTS
                      ? t('projectionsTab.accounts')
                      : activeScenario.scenarioType === simulationScenarioTypes.SUBSTITUTE
                      ? t('projectionsTab.substitute')
                      : activeScenario.scenarioType === simulationScenarioTypes.COMPOSITION
                      ? t('projectionsTab.composition')
                      : activeScenario.scenarioType === simulationScenarioTypes.ALLOCATE
                      ? t('projectionsTab.allocate')
                      : t('projectionsTab.priceDefinitions')}
                  </span>
                )}
              </div>
              <div className={classes.editorCard__sub}>
                {activeScenario.isBaseline
                  ? (isComparing ? t('projectionsTab.compareModeReadOnly') : t('projectionsTab.baselineHint'))
                  : t('projectionsTab.adjustParamsHint')}
              </div>
            </div>
            {activeScenario.isBaseline && !isComparing && (
              <div className={classes.baselineToggle}>
                <button
                  className={`${classes.baselineToggleBtn} ${baselineParamType === simulationScenarioTypes.PRICE_DEFINITIONS ? classes.baselineToggleBtn_active : ""}`}
                  onClick={() => setBaselineParamType(simulationScenarioTypes.PRICE_DEFINITIONS)}
                >
                  {t('projectionsTab.priceDefinitions')}
                </button>
                <button
                  className={`${classes.baselineToggleBtn} ${baselineParamType === simulationScenarioTypes.ACCOUNTS ? classes.baselineToggleBtn_active : ""}`}
                  onClick={() => setBaselineParamType(simulationScenarioTypes.ACCOUNTS)}
                >
                  {t('projectionsTab.accounts')}
                </button>
              </div>
            )}
          </div>
          {isActiveComposition ? (
            <CompositionEditorBody
              bundles={compositionBundles}
              activeScenario={activeScenario}
              editComposition={editComposition}
              onChange={handleUpdateComposition}
              onReset={handleResetBundleComposition}
              onResetAll={handleResetAllComposition}
              onUndo={handleUndoComposition}
              onRedo={handleRedoComposition}
              canUndo={compositionHistory.past.length > 0}
              canRedo={compositionHistory.future.length > 0}
              substituteOptions={substituteOptions}
              readOnly={readOnly}
              currency={currency}
            />
          ) : isActiveAllocate ? (
            <AllocationEditorBody
              lineAgg={lineAgg}
              projectOrder={projectOrder}
              activeScenario={activeScenario}
              editAllocation={editAllocation}
              onChange={setEditAllocation}
              readOnly={readOnly}
              currency={currency}
            />
          ) : isActiveSubstitute ? (
            <SubstitutionEditorBody
              defs={allParams}
              activeScenario={activeScenario}
              editSubs={editSubs}
              onChange={handleUpdateSub}
              substituteOptions={substituteOptions}
              readOnly={readOnly}
              currency={currency}
            />
          ) : (
          <table className={`${classes.paramTable} no-stripes`} style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col />
              <col style={{ width: SIM_COL_W }} />
              {showScenarioCol && (isComparing
                ? orderedSimCols.map((s) => <col key={s.id} style={{ width: SIM_COL_W }} />)
                : <col style={{ width: SIM_COL_W }} />
              )}
              <col />
            </colgroup>
            <thead>
              <tr>
                <th className={classes.paramTh}>{t('projectionsTab.parameter')}</th>
                <th className={classes.paramTh}>{t('projectionsTab.current')}</th>
                {showScenarioCol && (isComparing
                  ? orderedSimCols.map((s) => {
                      const isActiveCol = s.id === activeScenarioId;
                      const isBestCol = bestScenarioId === s.id;
                      return (
                        <th key={s.id} className={`${classes.paramTh} ${isActiveCol ? classes.paramTh_active : (isBestCol ? classes.paramTh_best : classes.paramTh_compare)}`} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {isBestCol && <Trophy size={11} style={{ flexShrink: 0, color: isActiveCol ? "#d97706" : "rgba(255,255,255,0.9)" }} />}
                            {s.name}
                          </span>
                        </th>
                      );
                    })
                  : <th className={classes.paramTh}>{t('projectionsTab.scenario')}</th>
                )}
                <th className={classes.paramTh}>{t('projectionsTab.delta')}</th>
              </tr>
            </thead>
            <tbody>
              {paramRows.length === 0 && (
                <tr>
                  <td colSpan={paramColCount} className={classes.paramEmpty}>
                    {t('projectionsTab.noParameters')}
                  </td>
                </tr>
              )}
              {paramRows.length > 0 && (() => {
                const isPriceDef = effectiveParamType === simulationScenarioTypes.PRICE_DEFINITIONS;
                const hasMultipleGroups = new Set(paramRows.map((p) => p.group)).size > 1;
                let lastGroup = null;
                let lastCategory = null;
                return paramRows.flatMap((p) => {
                  const rows = [];
                  if (p.group && (hasMultipleGroups || (isPriceDef && p.group !== "Uncategorized")) && p.group !== lastGroup) {
                    lastGroup = p.group;
                    lastCategory = null;
                    rows.push(
                      <tr key={`group-${p.group}`} className={classes.paramGroupRow}>
                        <td colSpan={paramColCount} className={`${classes.paramGroupCell} ${p.group === "Revenue" ? classes.paramGroupCell_revenue : p.group === "Cost" ? classes.paramGroupCell_cost : ""}`}>{p.group === "Revenue" ? t('projectionsTab.revenue') : p.group === "Cost" ? t('projectionsTab.costs') : p.group}</td>
                      </tr>
                    );
                  }
                  if (!isPriceDef && p.categoryName && p.categoryName !== lastCategory) {
                    lastCategory = p.categoryName;
                    rows.push(
                      <tr key={`cat-${p.group}-${p.categoryName}`} className={classes.paramCategoryRow}>
                        <td colSpan={paramColCount} className={classes.paramCategoryCell}>{p.categoryName}</td>
                      </tr>
                    );
                  }
                  const comparisonCols = compareSimCols.map((simCol) => ({
                    value: getScenarioParamVal(simCol, p.id, p.currentValue),
                    baseline: p.currentValue,
                    isRevenue: p.group === "Revenue",
                  }));
                  rows.push(
                    <ParamRow
                      key={p.id}
                      param={p}
                      editValue={p.editVal}
                      onChange={handleUpdateParam}
                      onReset={handleResetParam}
                      disabled={activeScenario.isBaseline}
                      showEditCol={!activeScenario.isBaseline}
                      currency={currency}
                      decimals={isPriceDef ? 2 : 0}
                      uom={isPriceDef ? p.uom : undefined}
                      showCategory={false}
                      comparisonCols={comparisonCols}
                    />
                  );
                  return rows;
                });
              })()}
            </tbody>
          </table>
          )}
          {!activeScenario.isBaseline && (
            <div className={classes.editorCard__footer}>
              <Button variant="ghost" size="small" onClick={handleResetEdits} disabled={!hasUnsavedEdits}>
                {t('common.reset')}
              </Button>
              <div className={classes.editorCard__footerRight}>
                {isActiveAllocate ? (
                  <Button
                    className={classes.applyBtn}
                    onClick={handleApply}
                    startIcon={appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits ? <Check /> : null}
                  >
                    {appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits
                      ? t('projectionsTab.applied')
                      : t('projectionsTab.applyToInsightsPL')}
                  </Button>
                ) : (
                  <Button
                    className={classes.applyBtn}
                    onClick={handleApply}
                    startIcon={appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits ? <Check /> : null}
                  >
                    {appliedScenarioId === activeScenarioId && activeProjection && !hasUnsavedEdits
                      ? t('projectionsTab.applied')
                      : t('projectionsTab.applyToAllTabs')}
                  </Button>
                )}
                {!readOnly && (
                  <Button variant="primary" loading={saving} onClick={handleSave}>
                    {t('projectionsTab.saveScenario')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* P&L IMPACT TABLE */}
        <div className={classes.plCard}>
          <div className={classes.plCard__head}>
            <div className={classes.plCard__title}>{t('projectionsTab.plStatement')}</div>
          </div>
          <div className={classes.plCard__tableWrap}>
            <PLTable
              baseline={baselinePL}
              orderedSimCols={orderedSimCols}
              activeScenarioId={activeScenario.id}
              currency={currency}
              onReorderCols={setSimColOrder}
              bestScenarioId={bestScenarioId}
            />
          </div>
        </div>
      </div>

      <NewScenarioModal
        open={modalOpen}
        onConfirm={handleNewScenario}
        onCancel={() => setModalOpen(false)}
      />
      <DeleteScenarioDialog
        open={deleteScenarioId !== null}
        scenarioName={scenarioList.find((s) => s.id === deleteScenarioId)?.name ?? ""}
        onConfirm={() => { handleDeleteScenario(deleteScenarioId); setDeleteScenarioId(null); }}
        onClose={() => setDeleteScenarioId(null)}
      />
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: 500,
    gap: 0,
  },

  // Sidebar
  sidebar: {
    width: 260,
    flexShrink: 0,
    background: "#fff",
    borderRight: "1px solid var(--color-border-light)",
    paddingRight: "16px",
    alignSelf: "flex-start",
    position: "sticky",
    top: 76,
    maxHeight: "calc(100vh - 76px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  sidebar__list: {
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
    paddingRight: 4,
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-button": { display: "none" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#d1d5db", borderRadius: 4 },
    "&::-webkit-scrollbar-thumb:hover": { background: "#9ca3af" },
  },
  sidebar__titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  sidebar__title: {
    fontSize: 12,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "var(--color-text-secondary)",
  },
  sidebar__loading: {
    fontSize: 13,
    color: "#9ca3af",
    padding: "8px 0",
  },

  // Scenario cards
  scenarioCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: "#c8922a" },
  },
  scenarioCard_active: {
    borderColor: "#c8922a",
    background: "#fdf5e8",
  },
  scenarioCard_comparing: {
    borderColor: "#1a56db",
    background: "#e8f0fd",
    "&:hover": { borderColor: "#1a56db" },
  },
  scenarioCard__nameRow: {
    minHeight: "24px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 6,
    marginBottom: 4,
  },
  scenarioCard__name: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  scenarioCard__tag: {
    fontSize: 10,
    color: "#9ca3af",
  },
  scenarioCard__appliedBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#15803d",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 4,
    padding: "1px 6px",
  },
  scenarioCard__bestBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    borderRadius: 4,
    padding: "1px 6px",
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
  },
  scenarioCard__editBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    borderRadius: 4,
    border: "none",
    background: "var(--color-bg-card)",
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      background: "var(--color-bg-disabled)",
    },
  },
  scenarioCard__summary: {
    fontSize: 12,
    color: "#6b7280",
    marginBottom: 4,
  },
  scenarioCard__actions: {
    display: "flex",
    gap: 4,
    marginTop: 6,
  },

  sidebar__filterArea: {
    marginBottom: 10,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  addScenario: {
    border: "1px dashed #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    textAlign: "center",
    fontSize: 14,
    color: "var(--color-text-secondary)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    transition: "all 0.15s",
    "&:hover": { borderColor: "#c8922a", color: "#c8922a" },
  },

  compareBar: {
    background: "#07142d",
    borderRadius: 8,
    padding: "10px 12px",
    marginTop: 4,
  },
  compareBar__title: {
    fontSize: 11,
    color: "rgba(255,255,255,0.6)",
    marginBottom: 6,
  },
  compareBar__item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 12,
    color: "#fff",
    marginBottom: 3,
  },
  compareBar__clear: {
    fontSize: 11,
    color: "rgba(255,255,255,0.4)",
    cursor: "pointer",
    background: "none",
    border: "none",
    textDecoration: "underline",
    marginTop: 6,
    fontFamily: "inherit",
    "&:hover": { color: "#fff" },
  },

  // Main area
  main: {
    flex: 1,
    minWidth: 0,
    paddingLeft: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  // Editor card
  editorCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  editorCard__head: {
    padding: "16px",
    borderBottom: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  editorCard__sub: {
    fontSize: 12,
    color: "#6b7280",
    marginTop: 2,
  },
  typeChip: {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 4,
    background: "#fdf5e8",
    border: "1px solid #c8922a",
    color: "#c8922a",
  },

  // Param table
  paramTable: {
    width: "100%",
    borderCollapse: "collapse",
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  paramTh: {
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    padding: "8px 16px",
    textAlign: "left",
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  paramRow: {
    "&:hover $paramCell": { background: "#f9fafb" },
    "&:last-child $paramCell": { borderBottom: "none" },
  },
  paramCell: {
    padding: "8px 16px",
    borderBottom: "1px solid #DCDCDC",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  paramCell_mono: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 14,
    color: "var(--color-text-secondary)",
  },
  param__name: {
    fontSize: 13,
    color: "var(--color-text-primary)",
  },
  param__cat: {
    fontSize: 11,
    color: "var(--color-text-secondary)",
  },
  param__inputWrap: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  param__unit: {
    fontSize: 12,
    color: "var(--color-text-secondary)",
  },
  param__input: {
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "4px 8px",
    fontSize: 14,
    width: 128,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: "var(--color-primary-light)" },
  },
  param__input_changed: {
    borderColor: "var(--color-primary-light)",
    background: "var(--color-primary-tint)",
  },
  param__resetBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    flexShrink: 0,
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#f9fafb",
    color: "#9ca3af",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.15s",
    "&:hover": {
      borderColor: "#c8922a",
      color: "#c8922a",
      background: "#fdf5e8",
    },
  },
  editorCard__footer: {
    padding: "16px",
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  editorCard__footerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  applyBtn: {
    "&&": {
      background: "#07142d",
      color: "#fff",
      fontWeight: 500,
    },
    "&&:hover": { opacity: 0.85 },
  },
  // Active projection indicator banner
  projectionBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 10,
    padding: "10px 16px",
  },
  projectionBanner__left: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#15803d",
  },
  projectionBanner__icon: {
    color: "#16a34a",
    flexShrink: 0,
  },
  paramGroupRow: {
    background: "#f9fafb",
  },
  paramGroupCell: {
    padding: "6px 16px",
    fontSize: 11,
    fontWeight: 600,
    color: "#6b7280",
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    borderBottom: "1px solid #DCDCDC",
    borderTop: "1px solid #DCDCDC",
  },
  paramGroupCell_revenue: { color: "#16a34a" },
  paramGroupCell_cost: { color: "#dc2626" },
  paramCategoryRow: {
    background: "#fff",
  },
  paramCategoryCell: {
    padding: "5px 20px",
    fontSize: 11,
    fontWeight: 600,
    color: "#374151",
    borderBottom: "1px solid #DCDCDC",
  },
  paramEmpty: {
    padding: "20px 16px",
    fontSize: 13,
    color: "#9ca3af",
    textAlign: "center",
  },

  // P&L card
  plCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    overflow: "hidden",
  },
  plCard__head: {
    padding: "14px 16px",
    borderBottom: "1px solid #f3f4f6",
  },
  plCard__title: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
  },
  plCard__tableWrap: {
    overflowX: "auto",
  },

  // P&L table
  plTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
    "& tr:nth-child(even) td": { background: "#fff" },
  },
  plTh: {
    padding: "8px 14px",
    textAlign: "right",
    fontSize: 11,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    borderBottom: "1px solid #DCDCDC",
    background: "#fff",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  plTh_baseline: {
    background: "#f9fafb",
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  plTh_scenario: {
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  plTh_compare: {
    background: "var(--color-bg-brand)",
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    userSelect: "none",
  },
  plTh_best: {
    background: "#d97706",
    color: "#fff",
    fontWeight: 500,
    userSelect: "none",
  },
  plTh_dragOver: {
    boxShadow: "inset 0 0 0 2px rgba(255,255,255,0.8)",
    opacity: 0.8,
  },
  plTh_delta: {
    background: "rgba(0,0,0,0.02)",
    color: "var(--color-text-secondary)",
  },
  plRow: {
    "&:hover $plCell": { background: "#f9fafb" },
  },
  plSectionRow: {
    background: "#fff",
  },
  plSubtotalRow: {
    "& td": { background: "var(--color-bg-page) !important" },
  },
  plTotalRow: {
    background: "var(--color-bg-card)",
    "& $plCell": {
      borderTop: "2px solid var(--color-primary)",
    },
  },
  plCell: {
    padding: "8px 14px",
    borderBottom: "1px solid #DCDCDC",
    textAlign: "right",
    fontFamily: "'DM Mono', 'Courier New', monospace",
    whiteSpace: "nowrap",
    overflow: "hidden",
    "&:first-child": {
      textAlign: "left",
      fontFamily: "inherit",
      color: "var(--color-text-primary)",
    },
  },
  plCell_section: {
    fontWeight: 600,
    fontSize: 12,
    color: "#07142d",
  },
  plCell_section_revenue: { color: "#16a34a !important" },
  plCell_section_cost: { color: "#dc2626 !important" },
  plCell_total: {
    fontWeight: 600,
    fontFamily: "'DM Mono', 'Courier New', monospace",
    fontSize: 14,
    color: "var(--color-primary)",
  },
  plCell_delta: {
    background: "rgba(0,0,0,0.02)",
  },
  plCategoryRow: {
    "&:hover $plCell": { background: "#f3f4f6" },
  },
  plCell_category: {
    fontWeight: 600,
    fontSize: 14,
    color: "#374151",
  },
  plCell_categoryAmount: {
    fontWeight: 600,
  },
  plCell_account: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    paddingLeft: 16,
  },
  plCell_account_inner: {
    display: "inline-flex",
    alignItems: "center",
  },
  plCell_account_icon: {
    marginRight: 6,
    color: "var(--color-text-secondary)",
  },
  plVal_pos: { color: "var(--color-text-success)", fontWeight: 500 },
  plVal_neg: { color: "var(--color-text-danger)", fontWeight: 500 },

  // Dialog (matches PriceDefinitionDialog design system)
  dialogPaper: {
    width: "400px !important",
    height: "auto",
    maxHeight: "80vh !important",
    borderRadius: "6px !important",
  },
  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },
  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",
    padding: "0px !important",
  },
  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",
    padding: "3px !important",
    height: "25px !important",
    borderRadius: "3px",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },
  },
  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",
    "&::-webkit-scrollbar": { display: "none" },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },
  dialog__content: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    width: "100%",
  },
  dialog__sub: {
    fontSize: "14px",
    color: "#6b7280",
    margin: "0 0 16px 0",
  },
  inputContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  labelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  labelText: {
    fontSize: "14px",
    color: "var(--color-text-primary)",
    opacity: 0.8,
  },
  typeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  typeBtn: {
    flex: "1 1 auto",
    minWidth: "fit-content",
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #EDEDED",
    background: "#F8F8F8",
    fontSize: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#6b7280",
    transition: "all 0.15s",
    "&:hover": { borderColor: "var(--color-text-primary)" },
  },
  typeBtn_active: {
    borderColor: color.selected.border,
    background: color.selected.bg,
    color: color.selected.text,
    fontWeight: 500,
  },
  baselineToggle: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  baselineToggleBtn: {
    padding: "8px 12px",
    borderRadius: "4px",
    border: "1px solid #EDEDED",
    background: "#F8F8F8",
    fontSize: "14px",
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#6b7280",
    whiteSpace: "nowrap",
    transition: "all 0.15s",
    "&:hover": { borderColor: "var(--color-primary-tint)" },
  },
  baselineToggleBtn_active: {
    borderColor: "var(--color-primary-tint)",
    background: "var(--color-primary-subtle)",
    color: "var(--color-primary)",
    fontWeight: 500,
  },
  paramTh_active: {
    background: "#fdf5e8",
    color: "#c8922a",
    fontWeight: "normal",
    textAlign: "left",
  },
  paramTh_compare: {
    background: "var(--color-bg-brand)",
    color: "var(--color-text-secondary)",
    fontWeight: "normal",
    textAlign: "right",
  },
  paramTh_best: {
    background: "#d97706",
    color: "#fff",
    fontWeight: "normal",
    textAlign: "right",
  },
}));
